import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import DashboardCustomizer, { type CustomChart, type CustomChartType, type CustomDataSource, type WidgetDef } from './DashboardCustomizer';
import { type DashboardRole, type RoleConfig, ROLE_CONFIGS,
   detectDashboardRole, widgetStorageKey } from './DashboardRoleConfig';
import { type DynamicChartConfig } from './ChartRegistry';
import DynamicChart from './DynamicChart';
import DynamicChartBuilder from './DynamicChartBuilder';
import DrillDownModal, { type DrillDownColumn } from './DrillDownModal';
import {
  loadDashboardChartsApi,
  saveDashboardChartApi,
  deleteDashboardChartApi,
  type SavedChart,
} from './dashboardChartsApi';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getV2Token } from 'src/utils/v2AuthUtils';
import { validateToken } from 'src/utils/tokenValidator';
import { getUserData } from 'src/utils/getUserInfo';
import { usePagePermission, normalizePageName } from 'src/hooks/usePagePermission';
import { getUserRolePermissions } from 'src/services/userServicer';
import { FaExclamationCircle, FaCheckCircle, FaTasks, 
  FaExclamationTriangle, FaInfoCircle, FaExternalLinkAlt, 
  FaRegCheckCircle, FaRegCircle, FaBan,
   FaRegClock, FaFilter, FaTimes, FaSpinner } from 'react-icons/fa'; // Example icons

import { HiOutlineExternalLink } from 'react-icons/hi';
import cx from 'classnames';
import SimplePieChart from "./PieChartSecurity";
import ComplianceProgressChart from "./ComplianceProgressChart";
import { PiChartDonutThin } from "react-icons/pi";
import { RiProgress2Line } from "react-icons/ri";
import { LuShieldCheck } from "react-icons/lu";


import {
  Row,
  Col,
  Button,
  ButtonGroup,
  Breadcrumb,
  BreadcrumbItem,
  Progress,
  Badge,
  Table,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import Widget from '../../components/Widget/Widget';
import LineChart from '../../components/charts/charts/LineChart';
import BarChart from '../../components/charts/charts/BarChart';
import PieChart from '../../components/charts/charts/PieChart';
import './Dashboard.css';
// Superset embed is disabled (the embedding effect below is commented out), so the SDK
// is not imported — it only pulled weight into the bundle.
// import { embedDashboard } from "@superset-ui/embedded-sdk";

import { fetchComplianceActions, fetchRecentAssignments, fetchSurveysForFunctions,
   getComplianceDashboard, getComplianceForEachStandardDashboard, getUsersTasksDashboard,
   getUnassignedDashboard,
   fetchComplianceActionsOverview, fetchSurveyAssignmentsOverview,
   fetchDashboardBucketPage, fetchWorkloadTasksPage,
 } from '../../services/dashboards';
import { getMyFunctionIds } from 'src/services/functions';
import { getRemediations } from 'src/services/remediation';
import { PRIORITIES as REMEDIATION_PRIORITIES, fmtDate as fmtRemediationDate } from 'src/pages/compliance/remediation/remediationShared';
import { getReport } from 'src/services/reference';
import { useTranslation } from 'react-i18next';
import LoadingScreen from 'src/Loading/LoadingScreen';
// fetchComplianceStatus is NOT imported: a local stub of the same name (below) shadows it,
// so the imported version could never run — importing it only made that confusing.
import { fetchCountUsersParticipateSurveys,
  fetchNumControlPerCompliance,
  fetchTotalSurveyResponsesPage, fetchSurveyParticipantsPage, fetchSurveysPage,
  fetchSurveyConductedTime, fetchTaskCompletionRate, fetchTotalCountSurveys, fetchTotalSurveyResponse,
  getComplianceProgressOverTime} from 'src/services/chartDashboardSecurity';
import LineChartSurvey from './lineChartSecurity';
import ComplianceBarChart from './BarChartSecurity';
import { GrLineChart } from 'react-icons/gr';
import { HiOutlineChartBar, HiOutlineChartPie, HiOutlineDocumentText, HiOutlineUsers } from 'react-icons/hi2';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
// Superset guest token — only consumer was getToken5(), whose only caller is the
// commented-out embed effect. Nothing fetches it now.
// import { getGuestToken5 } from 'src/services/userServicer';
// getObjectivesFromBackend / getControlMapperFromBackend: both call sites are disabled
// (endpoints 404 on this backend); only the ApiObjective type is still used.
import { ApiObjective } from 'src/services/objectiveAIServices';
import {
  PieChart as RcPieChart,
  Pie as RcPie,
  Cell as RcCell,
  BarChart as RcBarChart,
  Bar as RcBar,
  XAxis as RcXAxis,
  YAxis as RcYAxis,
  CartesianGrid as RcCartesianGrid,
  Tooltip as RcTooltip,
  Legend as RcLegend,
  ResponsiveContainer as RcResponsiveContainer,
  RadarChart as RcRadarChart,
  Radar as RcRadar,
  PolarGrid as RcPolarGrid,
  PolarAngleAxis as RcPolarAngleAxis,
  PolarRadiusAxis as RcPolarRadiusAxis,
  RadialBarChart as RcRadialBarChart,
  RadialBar as RcRadialBar,
  LabelList as RcLabelList,
} from 'recharts';

// Compliance Objectives Overview palette (priority ranks 1..n, effort High/Medium/Low)
const OBJECTIVE_PRIORITY_COLORS = ['#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6', '#14B8A6'];
const OBJECTIVE_EFFORT_COLORS = ['#EF4444', '#F59E0B', '#22C55E', '#3B82F6'];
// Control Mapper + Assessment palettes
const REGULATION_BAR_COLORS = ['#14B8A6', '#3B82F6', '#F59E0B', '#F97316', '#8B5CF6', '#EC4899', '#22C55E'];
const CONTROL_DIST_COLORS = ['#3B82F6', '#22C55E'];
const OWNER_BAR_COLOR = '#3B82F6';
const CATEGORY_SCORE_COLORS = ['#1f7c50', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1'];
const ASSESSMENT_SCORE_COLOR = '#1f7c50';
const ASSESSMENT_COMPLETION_COLOR = '#3b82f6';

// Remediation widgets — one solid tone per priority, matching the rem-priority-*
// badges on the Remediation page so the same plan reads the same colour on both.
const REMEDIATION_PRIORITY_COLORS: Record<string, string> = {
  low: '#64748B',
  medium: '#3B82F6',
  high: '#F59E0B',
  critical: '#EF4444',
};

/** Highest priority first — the order the attention list and the donut use. */
const REMEDIATION_PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

/**
 * Soft badge tones for the watchlist, mirroring .rem-priority-* in remediation.css.
 * Repeated as inline styles rather than imported: that stylesheet is the
 * Remediation page's own, and pulling it into the dashboard bundle would put its
 * table/layout rules on every widget here.
 */
const REMEDIATION_PRIORITY_BADGE: Record<string, { bg: string; fg: string; border: string }> = {
  low:      { bg: '#f2f5f7', fg: '#5b6b73', border: '#dfe6ea' },
  medium:   { bg: '#eaf2fd', fg: '#1a5fb4', border: '#c5dcf7' },
  high:     { bg: '#fff2e6', fg: '#b45309', border: '#ffd9b3' },
  critical: { bg: '#fdecec', fg: '#b3261e', border: '#f8cdcb' },
};

const priorityBadgeStyle = (priority: string): React.CSSProperties => {
  const tone = REMEDIATION_PRIORITY_BADGE[priority] ?? { bg: '#F3F4F6', fg: '#6B7280', border: '#E5E7EB' };
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: 999,
    fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
  };
};

/**
 * The list is paged server-side and the priority filter below is applied in the
 * browser, so the widgets ask for one page big enough to hold the whole register.
 * 500 is the cap the API enforces on perPage; a tenant past it gets a truncation
 * note on the widget rather than silently short counts.
 */
const REMEDIATION_FETCH_LIMIT = 500;

/** Clamp a number to an integer percentage in [0, 100]. */
const clampPct = (n: number): number => {
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
};

/** Truncate a long label for chart axes/legends. */
const truncateLabel = (name: string, max = 16): string => {
  const s = String(name ?? '');
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
};

// ─────────────────────────────────────────────────────────────────────────────
// SEED / DEMO FALLBACK DATA
// Used only when the backend returns no data so every chart renders something.
// Real API data always takes precedence — delete these constants (and the
// `resolved*` fallbacks below) once the tenant has live data.
// ─────────────────────────────────────────────────────────────────────────────
const SEED_OBJECTIVES: ApiObjective[] = [
  { objective_id: 'seed-1', objective_name: 'Strengthen digital payment security', description: 'Harden digital payment and banking platforms against emerging threats.', estimated_effort: 'High', priority_rank: 1, regulatory_reference: 'Digital Payments and Banking Regulations', suggested_start_date: '2026-01-05', suggested_completion_date: '2026-01-15' },
  { objective_id: 'seed-2', objective_name: 'Adopt CBE cybersecurity framework', description: 'Align controls with the CBE Financial Cybersecurity Framework.', estimated_effort: 'High', priority_rank: 1, regulatory_reference: 'CBE Financial Cybersecurity Framework', suggested_start_date: '2026-02-01', suggested_completion_date: '2026-03-20' },
  { objective_id: 'seed-3', objective_name: 'Enhance AML/CFT monitoring', description: 'Expand fraud combating and AML/CFT supervision processes.', estimated_effort: 'High', priority_rank: 2, regulatory_reference: 'AML CFT Supervision & Fraud Combating', suggested_start_date: '2026-02-10', suggested_completion_date: '2026-03-25' },
  { objective_id: 'seed-4', objective_name: 'Implement PDPL data protection', description: 'Meet data protection and privacy framework (PDPL) requirements.', estimated_effort: 'Medium', priority_rank: 2, regulatory_reference: 'Data Protection and Privacy Framework (PDPL)', suggested_start_date: '2026-04-01', suggested_completion_date: '2026-06-10' },
  { objective_id: 'seed-5', objective_name: 'Advance sustainability reporting', description: 'Establish reporting aligned with CBE Sustainability Regulations.', estimated_effort: 'Low', priority_rank: 3, regulatory_reference: 'CBE Sustainability Regulations', suggested_start_date: '2026-07-01', suggested_completion_date: '2026-09-05' },
];

const SEED_CONTROL_MAPPER: { individual: any[]; grouped: any[] } = {
  grouped: [
    {
      name: 'Comprehensive Risk and AML/CFT Data Collection and Submission',
      description: 'Group of controls covering regulatory data collection and submission.',
      objective: 'To expand risk reporting by developing processes for collecting accurate national data.',
      suggested_owner_role_department: { name: 'Supporting Functions' },
      mapped_controls: [
        { code: 'C0022', name: 'Risk Data Collection and Submission', description: 'Develop internal processes to collect and submit required data to the CBE; ensure accuracy and timeliness.', objective: 'To expand risk reporting by developing processes for collecting national data.', mandatoryType: 'mandatory' },
        { code: 'C0031', name: 'AML/CFT Transaction Reporting', description: 'Establish automated reporting of suspicious transactions to the regulator within mandated timeframes.', objective: 'To strengthen AML/CFT compliance through timely reporting.', mandatoryType: 'mandatory' },
      ],
    },
  ],
  individual: [
    { control: { code: 'C0012', name: 'Digital Insurance Broker Compliance', description: "For digital insurance brokers, obtain FRA approval, integrate platforms with insurers' systems using secure APIs, perform penetration and vulnerability testing, and comply with cybersecurity requirements.", objective: 'To achieve secure digital platforms by meeting specific FRA requirements for digital insurance systems and security.', mandatoryType: 'mandatory' }, suggested_owner_role_department: { name: 'Supporting Functions' } },
    { control: { code: 'C0003', name: 'Data Encryption, Retention, and Disposal', description: 'Encrypt sensitive authentication data (SAD) and primary account numbers (PAN), and define retention and disposal periods.', objective: 'To ensure data protection and privacy by securing sensitive data throughout its lifecycle.', mandatoryType: 'mandatory' }, suggested_owner_role_department: { name: 'Unassigned' } },
    { control: { code: 'C0002', name: 'Privileged User Activity Monitoring', description: 'Maintain logs of all privileged user activities and review them regularly.', objective: 'To require unique user identification and maintain accountability for actions in critical systems.', mandatoryType: 'mandatory' }, suggested_owner_role_department: { name: 'Unassigned' } },
  ],
};

const SEED_ASSESSMENT = {
  overallScore: 32,
  maxPossibleScore: 1027,
  actualScore: 328,
  answeredQuestions: 16,
  totalQuestions: 52,
  categories: [
    { categoryId: 'seed-c1', categoryName: 'New English Question Text', answeredQuestions: 7, totalQuestions: 7, score: 100 },
    { categoryId: 'seed-c2', categoryName: 'Roles & Responsabilities', answeredQuestions: 3, totalQuestions: 3, score: 50 },
    { categoryId: 'seed-c3', categoryName: 'Technology', answeredQuestions: 2, totalQuestions: 7, score: 24 },
    { categoryId: 'seed-c4', categoryName: 'Strategy & Governance', answeredQuestions: 3, totalQuestions: 15, score: 39 },
    { categoryId: 'seed-c5', categoryName: 'Compliance', answeredQuestions: 0, totalQuestions: 6, score: 0 },
    { categoryId: 'seed-c6', categoryName: 'Security Operations', answeredQuestions: 1, totalQuestions: 14, score: 9 },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// WIDGET CATALOG — all available overview widgets
// ─────────────────────────────────────────────────────────────────────────────
const WIDGET_DEFS: readonly WidgetDef[] = [
  { id: 'kpi-cards',        label: 'KPI Summary Cards',       icon: '📊', desc: 'Task counts by status',                    defaultW: 12 },
  { id: 'status-donut',     label: 'Task Status Donut',       icon: '🍩', desc: 'Status distribution pie chart',            defaultW: 5  },
  { id: 'standards-bar',    label: 'Compliance by Standard',  icon: '📋', desc: 'Per-standard compliance stacked bar',      defaultW: 7  },
  { id: 'compliance-gauge', label: 'Compliance Rate Gauge',   icon: '🎯', desc: 'Overall compliance percentage gauge',      defaultW: 4  },
  { id: 'completion-gauge', label: 'Task Completion Gauge',   icon: '✅', desc: 'Task completion percentage gauge',         defaultW: 4  },
  { id: 'quick-summary',    label: 'Quick Summary',           icon: '⚡', desc: 'Standards, actions, surveys overview',    defaultW: 4  },
  { id: 'remediation-kpis',     label: 'Remediation Summary',     icon: '🛠️', desc: 'Remediation plans by state + overdue',  defaultW: 12 },
  { id: 'remediation-priority', label: 'Remediation by Priority', icon: '🚦', desc: 'Plan count per priority level',        defaultW: 5  },
  { id: 'remediation-attention',label: 'Remediation Watchlist',   icon: '⏰', desc: 'Overdue and highest-priority plans',   defaultW: 7  },
  { id: 'workload',         label: 'Team Workload',           icon: '👥', desc: 'In-progress tasks per team member',        defaultW: 12, adminOnly: true    },
  { id: 'unassigned-work',  label: 'Unassigned Work',         icon: '🧩', desc: 'Items with no supervisor or no manager',   defaultW: 12, adminOnly: true    },
  { id: 'my-tasks',         label: 'My Tasks',                icon: '📝', desc: 'Your in-progress task list',               defaultW: 12, nonAdminOnly: true },
  { id: 'my-function-surveys', label: 'My Surveys',           icon: '📝', desc: 'Surveys linked to your function(s)',       defaultW: 12, nonAdminOnly: true },
] as const;

// Widgets limited to specific roles beyond adminOnly (e.g. unassigned work is for
// supervisor / leader / super_admin — not manager, even though manager is admin-tier).
const WIDGET_ROLES: Record<string, readonly DashboardRole[]> = {
  'unassigned-work': ['supervisor', 'leader', 'super_admin'],
};
const widgetAllowedForRole = (id: string, role: DashboardRole): boolean =>
  !WIDGET_ROLES[id] || WIDGET_ROLES[id].includes(role);

// Saved chart metadata (isShared, isOwner) tracked alongside the config
type DynChartEntry = { config: DynamicChartConfig; isShared: boolean; isOwner: boolean; savedId: string };

function localDynKey(role: DashboardRole) { return widgetStorageKey(role) + '_dynamic_v2'; }

function localLoadDynamic(role: DashboardRole): DynChartEntry[] {
  try {
    const raw = localStorage.getItem(localDynKey(role));
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function localSaveDynamic(role: DashboardRole, entries: DynChartEntry[]): void {
  localStorage.setItem(localDynKey(role), JSON.stringify(entries));
}

function loadWidgetConfig(defs: readonly WidgetDef[], role: DashboardRole) {
  const storageKey = widgetStorageKey(role);
  const roleCfg = ROLE_CONFIGS[role];
  const defaultOrder = roleCfg.defaultOrder.length > 0 ? roleCfg.defaultOrder : defs.map(w => w.id);
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    // A widget added to the catalog after this layout was saved is missing from
    // the stored order. It still renders (getOrder falls back to last), but the
    // Customize panel only lists ids that ARE in the order — so append them here,
    // or a new widget can never be hidden, resized or moved.
    const order = Array.isArray(saved?.order)
      ? [...saved.order, ...defs.map(w => w.id).filter((id: string) => !saved.order.includes(id))]
      : defaultOrder;
    const widths = saved?.widths && typeof saved.widths === 'object' ? saved.widths : { ...roleCfg.defaultWidths };
    const hidden = new Set<string>(Array.isArray(saved?.hidden) ? saved.hidden : roleCfg.defaultHidden);
    const custom: CustomChart[] = Array.isArray(saved?.custom) ? saved.custom : [];
    return { order, widths, hidden, custom };
  } catch {
    return { order: defaultOrder, widths: { ...roleCfg.defaultWidths } as Record<string, number>, hidden: new Set<string>(roleCfg.defaultHidden), custom: [] as CustomChart[] };
  }
}

interface Post {
  id: number;
  title: string;
  updatedAt: string;
}

interface DashboardProps {
  posts: Post[];
  isFetching: boolean;
}
interface SurveyData {
  year: number;
  quarter: string;
  total_surveys_Assessment: number;
}

interface ControlPerCompliance {
  compliance_id: string;
  total_controls: number;
  compliance_code: string; // Use an underscore instead of a dot
}




function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // "Home" (the route's own gate, see RoleBasedRoute) is deliberately unblockable so no
  // group is ever left with nowhere to land. "Dashboard" is the separate, real permission
  // for THIS page's content — when it's show:false, the shell still loads but the widgets
  // are replaced with the standard not-allowed message, same pattern every other page uses.
  const { loading: dashboardPermissionLoading, notAllowed: dashboardNotAllowed } = usePagePermission('cs Dashboard');

  // Per-module "show" permission for the widget groups below — independent of
  // 'cs Dashboard' above (that one gates the whole page) and independent of
  // isVisible()/hiddenWidgets (that's the user's own show/hide preference,
  // which must never override an actual permission denial). One bulk fetch
  // (matching Compliance.tsx's pattern) rather than four separate
  // usePagePermission() calls, since getUserRolePermissions() already returns
  // every page's row in one response. Default true until it resolves, so a
  // module isn't hidden for a flash while this loads — none of these calls
  // grant anything the underlying data fetch doesn't already gate server-side,
  // they only add an earlier, explicit hide instead of waiting for a 403.
  const [modulePermissions, setModulePermissions] = useState({
    remediation: true,
    complianceTasks: true,
    standards: true,
    survey: true,
  });
  useEffect(() => {
    let cancelled = false;
    getUserRolePermissions().then((res: any) => {
      if (cancelled) return;
      const perms: any[] = res?.success ? res?.data?.permissions || [] : [];
      const showFor = (page: string) => {
        const row = perms.find((p) => normalizePageName(p?.page) === normalizePageName(page));
        return Boolean(row?.show);
      };
      setModulePermissions({
        remediation: showFor('Remediation'),
        complianceTasks: showFor('compliances-tasks'),
        standards: showFor('standards'),
        survey: showFor('survey'),
      });
    }).catch(() => {
      // A failed permission fetch shouldn't hide everything — the underlying
      // data fetches already 403 on their own if access is truly denied.
    });
    return () => { cancelled = true; };
  }, []);
  // The JWT's groupName is a point-in-time snapshot taken at login — if the user's group
  // changes afterward, this stays stale until they log in again. Seed instantly from the
  // token (no flicker on first paint), then correct from /auth/me once it resolves: that
  // endpoint always re-resolves groupName live from the DB (see JwtAuthGuard), so it's the
  // source of truth the rest of the app already relies on for "am I an admin now".
  const [roleInfo, setRoleInfo] = useState<{ dashboardRole: DashboardRole; roleConfig: RoleConfig; tokenName: string }>(() => {
    const token = getV2Token();
    const payload = token ? validateToken(token) : null;
    const g = payload?.groupName ?? '';
    const titleStr = payload?.title ?? '';
    const role = detectDashboardRole(g, titleStr);
    return { dashboardRole: role, roleConfig: ROLE_CONFIGS[role], tokenName: payload?.name ?? '' };
  });
  useEffect(() => {
    getUserData().then((user) => {
      if (!user) return;
      const role = detectDashboardRole(user.groupName ?? '', user.title ?? '');
      setRoleInfo({ dashboardRole: role, roleConfig: ROLE_CONFIGS[role], tokenName: user.name ?? '' });
    });
  }, []);
  const { dashboardRole, roleConfig, tokenName } = roleInfo;
  const isAdmin = dashboardRole === 'super_admin' || dashboardRole === 'leader' || dashboardRole === 'manager' || dashboardRole === 'supervisor';
  const roleWidgetDefs = useMemo(() => WIDGET_DEFS.filter((w) => widgetAllowedForRole(w.id, dashboardRole)), [dashboardRole]);
  // ── Widget customization state ─────────────────────────────────────────────
  const initCfg = useMemo(() => loadWidgetConfig(WIDGET_DEFS, dashboardRole), [dashboardRole]);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [widgetOrder,   setWidgetOrder]   = useState<string[]>(initCfg.order);
  const [widgetWidths,  setWidgetWidths]  = useState<Record<string, number>>(initCfg.widths);
  const [hiddenWidgets, setHiddenWidgets] = useState<Set<string>>(initCfg.hidden);
  const [customCharts,  setCustomCharts]  = useState<CustomChart[]>(initCfg.custom);

  const persistCfg = useCallback((
    order: string[], widths: Record<string, number>, hidden: Set<string>, custom: CustomChart[]
  ) => {
    localStorage.setItem(widgetStorageKey(dashboardRole), JSON.stringify({ order, widths, hidden: [...hidden], custom }));
  }, [dashboardRole]);

  const handleReorder = useCallback((newOrder: string[]) => {
    setWidgetOrder(newOrder);
    persistCfg(newOrder, widgetWidths, hiddenWidgets, customCharts);
  }, [widgetWidths, hiddenWidgets, customCharts, persistCfg]);

  const handleWidthChange = useCallback((id: string, w: number) => {
    setWidgetWidths(prev => {
      const next = { ...prev, [id]: w };
      persistCfg(widgetOrder, next, hiddenWidgets, customCharts);
      return next;
    });
  }, [widgetOrder, hiddenWidgets, customCharts, persistCfg]);

  const handleToggleHide = useCallback((id: string) => {
    setHiddenWidgets(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persistCfg(widgetOrder, widgetWidths, next, customCharts);
      return next;
    });
  }, [widgetOrder, widgetWidths, customCharts, persistCfg]);

  const handleAddCustom = useCallback((c: CustomChart) => {
    const next = [...customCharts, c];
    setCustomCharts(next);
    setWidgetOrder(prev => [...prev, c.id]);
    persistCfg([...widgetOrder, c.id], widgetWidths, hiddenWidgets, next);
  }, [customCharts, widgetOrder, widgetWidths, hiddenWidgets, persistCfg]);

  const handleRemoveCustom = useCallback((id: string) => {
    const next = customCharts.filter(c => c.id !== id);
    const nextOrder = widgetOrder.filter(o => o !== id);
    setCustomCharts(next);
    setWidgetOrder(nextOrder);
    persistCfg(nextOrder, widgetWidths, hiddenWidgets, next);
  }, [customCharts, widgetOrder, widgetWidths, hiddenWidgets, persistCfg]);

  const handleResetWidgets = useCallback(() => {
    const defaultOrder = roleConfig.defaultOrder.length > 0 ? roleConfig.defaultOrder : WIDGET_DEFS.map(w => w.id);
    const defaultWidths = { ...roleConfig.defaultWidths };
    const defaultHidden = new Set<string>(roleConfig.defaultHidden);
    setWidgetOrder(defaultOrder);
    setWidgetWidths(defaultWidths);
    setHiddenWidgets(defaultHidden);
    setCustomCharts([]);
    persistCfg(defaultOrder, defaultWidths, defaultHidden, []);
  }, [roleConfig, persistCfg]);

  const getW = useCallback((id: string, defaultW: number): number =>
    widgetWidths[id] ?? defaultW, [widgetWidths]);

  // Hard filter, not just a default: adminOnly/nonAdminOnly must hold regardless of
  // hiddenWidgets state (saved customization, stale localStorage from a prior role
  // detection, etc.) — mirrors the same (!w.adminOnly || isAdmin) && (!w.nonAdminOnly ||
  // !isAdmin) formula DashboardCustomizer already uses to decide what's offered in the
  // widget picker, so a personal widget like "My Surveys" can never re-appear for an
  // admin-tier role (super_admin included — 'admin_'/'admin' groups map to it too, see
  // detectDashboardRole) even if it was hidden/shown some other way before.
  const isVisible = useCallback((id: string): boolean => {
    if (hiddenWidgets.has(id)) return false;
    const def = WIDGET_DEFS.find((w) => w.id === id);
    if (def?.adminOnly && !isAdmin) return false;
    if (def?.nonAdminOnly && isAdmin) return false;
    if (!widgetAllowedForRole(id, dashboardRole)) return false;
    return true;
  }, [hiddenWidgets, isAdmin, dashboardRole]);

  const getOrder = useCallback((id: string): number => {
    const idx = widgetOrder.indexOf(id);
    return idx === -1 ? 999 : idx;
  }, [widgetOrder]);

  // ── Dynamic charts state (saved to backend, visible per user/group/function) ──
  const [dynEntries,        setDynEntries]        = useState<DynChartEntry[]>(() => localLoadDynamic(dashboardRole));
  const [dynamicBuilderOpen, setDynamicBuilderOpen] = useState(false);
  const dynamicCharts = dynEntries.map(e => e.config);

  // Load from API on mount — always sync local cache to match DB (including deletions)
  useEffect(() => {
    loadDashboardChartsApi().then(saved => {
      const entries: DynChartEntry[] = saved.map((s: SavedChart) => ({
        savedId: s.id,
        isShared: s.isShared,
        isOwner: s.isOwner,
        config: { ...s.config, id: s.id } as DynamicChartConfig,
      }));
      setDynEntries(entries);
      localSaveDynamic(dashboardRole, entries);
      if (entries.length > 0) {
        setWidgetOrder(prev => {
          const existing = new Set(prev);
          const newIds = entries.map(e => e.config.id).filter(id => !existing.has(id));
          return newIds.length ? [...prev, ...newIds] : prev;
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddDynamic = useCallback(async (cfg: DynamicChartConfig, isShared: boolean) => {
    const saved = await saveDashboardChartApi({ label: cfg.label, config: cfg, isShared });
    const finalId = saved?.id ?? cfg.id;
    const finalCfg = { ...cfg, id: finalId };
    const entry: DynChartEntry = { config: finalCfg, isShared, isOwner: true, savedId: finalId };
    const next = [...dynEntries, entry];
    setDynEntries(next);
    localSaveDynamic(dashboardRole, next);
    setWidgetOrder(prev => [...prev, finalId]);
    persistCfg([...widgetOrder, finalId], widgetWidths, hiddenWidgets, customCharts);
  }, [dynEntries, dashboardRole, widgetOrder, widgetWidths, hiddenWidgets, customCharts, persistCfg]);

  const handleRemoveDynamic = useCallback(async (id: string) => {
    const entry = dynEntries.find(e => e.config.id === id);
    if (entry?.isOwner) await deleteDashboardChartApi(entry.savedId);
    const next = dynEntries.filter(e => e.config.id !== id);
    const nextOrder = widgetOrder.filter(o => o !== id);
    setDynEntries(next);
    setWidgetOrder(nextOrder);
    localSaveDynamic(dashboardRole, next);
    persistCfg(nextOrder, widgetWidths, hiddenWidgets, customCharts);
  }, [dynEntries, dashboardRole, widgetOrder, widgetWidths, hiddenWidgets, customCharts, persistCfg]);
  // ── end dynamic charts state ─────────────────────────────────────────────────

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [complianceStatus, setComplianceStatus] = useState<any>([]);
  const [monthlyTasks, setMonthlyTasks] = useState<any>([]);
  const [complianceForEachStandard, setComplianceForEachStandard] = useState<any>([]);
  const [usersTasksDashboard, setUsersTasksDashboard] = useState<any>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [myFunctionSurveys, setMyFunctionSurveys] = useState<any[]>([]);
  const [myFunctionSurveysLoading, setMyFunctionSurveysLoading] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [surveyCount, setSurveyCount] = useState(0);
  const [totalResponses, setTotalResponses] = useState<number>(0);
  const [activeTab, setActiveTab] = useState('home');
  // Sub-tabs that split the Home dashboard's many charts into sections so the
  // page isn't one long scroll of widgets.
  const [chartTab, setChartTab] = useState<'overview' | 'objectives' | 'controls' | 'assessment' | 'surveys'>('overview');
  // Lazy-load: each tab's data is fetched only the first time that tab is opened.
  const loaded = useRef<Record<string, boolean>>({});
  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const [complianceActions, setComplianceActions] = useState([]);
  const [tableSearch, setTableSearch] = useState('');
  const [workloadUserModal, setWorkloadUserModal] = useState<{open: boolean; user: any | null}>({open: false, user: null});
  const [workloadSearch, setWorkloadSearch] = useState('');
  const [standardDrillModal, setStandardDrillModal] = useState<{open: boolean; loading: boolean; standardName: string; data: any[]}>({open: false, loading: false, standardName: '', data: []});
  const [standardSearch, setStandardSearch] = useState('');
  // Role-based standard + due-date-range filter (CISO / super_admin / supervisor).
  // Applied values drive the actual fetch; draft values are what the inputs show
  // while the user is still picking, only committed to "applied" on Apply Filters
  // (or cleared immediately by Clear All) so we don't refetch on every keystroke.
  const [selectedStandardId, setSelectedStandardId] = useState<string>('');
  const [dueDateFrom, setDueDateFrom] = useState<string>('');
  const [dueDateTo, setDueDateTo] = useState<string>('');
  const [draftStandardId, setDraftStandardId] = useState<string>('');
  const [draftDueDateFrom, setDraftDueDateFrom] = useState<string>('');
  const [draftDueDateTo, setDraftDueDateTo] = useState<string>('');
  const [filteredStdLoading, setFilteredStdLoading] = useState(false);
  const [filteredStdData, setFilteredStdData] = useState<any[]>([]);
  // Priority is a REMEDIATION filter: GET /remediation carries no priority param,
  // so it is applied to the fetched register in the browser (see remediationStats)
  // and deliberately left out of isRowFilterActive — that flag drives the
  // compliance-report refetch, which knows nothing about priority.
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [draftPriority, setDraftPriority] = useState<string>('');
  const isRowFilterActive = Boolean(selectedStandardId || dueDateFrom || dueDateTo);
  const isAnyFilterActive = isRowFilterActive || Boolean(priorityFilter);
  const filtersDirty =
    draftStandardId !== selectedStandardId ||
    draftDueDateFrom !== dueDateFrom ||
    draftDueDateTo !== dueDateTo ||
    draftPriority !== priorityFilter;

  const applyDraftFilters = () => {
    setSelectedStandardId(draftStandardId);
    setDueDateFrom(draftDueDateFrom);
    setDueDateTo(draftDueDateTo);
    setPriorityFilter(draftPriority);
  };

  const clearAllFilters = () => {
    setDraftStandardId(''); setDraftDueDateFrom(''); setDraftDueDateTo(''); setDraftPriority('');
    setSelectedStandardId(''); setDueDateFrom(''); setDueDateTo(''); setPriorityFilter('');
  };

  /** Set the priority filter straight from a chart click, skipping the draft step. */
  const applyPriorityFromChart = (priority: string) => {
    const next = priorityFilter === priority ? '' : priority;
    setDraftPriority(next);
    setPriorityFilter(next);
  };

  /** Human-readable label for the applied date range, for banners/drill titles. */
  const dueDateRangeLabel = (): string | null => {
    if (dueDateFrom && dueDateTo) return t('Dashboard.dateRangeBoth', { defaultValue: '{{from}} – {{to}}', from: dueDateFrom, to: dueDateTo }) as string;
    if (dueDateFrom) return t('Dashboard.dateRangeFromOnly', { defaultValue: 'From {{from}}', from: dueDateFrom }) as string;
    if (dueDateTo) return t('Dashboard.dueBeforeDateLabel', { defaultValue: 'Due before {{date}}', date: dueDateTo }) as string;
    return null;
  };

  // ── Filtered KPIs for the selected standard/date — every chart below that
  // shows task/compliance counts must read through activeDashData/activeTotalTasks
  // (falls back to the unfiltered dashboardData when no filter is picked)
  // rather than dashboardData directly, or the filter silently stops
  // at the 4 KPI cards and leaves the rest of the dashboard unfiltered.
  //
  // Gate on filteredStdLoading, NOT filteredStdData.length — a filter whose
  // tasks all belong to a (correctly excluded) deleted compliance legitimately
  // fetches to an empty array. Treating "0 results" the same as "haven't
  // fetched" made every chart silently fall back to the unfiltered global
  // view, which looked exactly like the filter did nothing.
  // The 6 named compliance_status values and the 2 named status values recognized
  // as their own KPI cards — kept in sync with KNOWN_TASK_STATUSES /
  // KNOWN_COMPLIANCE_STATUSES on the backend (getComplianceDashboardData) so the
  // client-side filtered breakdown partitions filteredStdData exactly the same
  // way the server partitions the unfiltered total (CN-UAT-17).
  const filteredKpis = useMemo(() => {
    if (!isRowFilterActive || filteredStdLoading) return null;
    const done        = filteredStdData.filter(r => r.status === 'Done').length;
    const inProg      = filteredStdData.filter(r => r.status === 'In Progress').length;
    const compliant   = filteredStdData.filter(r => r.compliance_status === 'Implemented').length;
    const nonCompliant = filteredStdData.filter(r => r.compliance_status === 'Not Implemented').length;
    const partially   = filteredStdData.filter(r => r.compliance_status === 'Partially Implemented').length;
    // "Everything else" buckets — guarantee (done+in_progress+taskNotStarted) and
    // (the 3 compliance buckets + unclassified) each sum to filteredStdData.length,
    // instead of silently dropping rows with an unrecognized/blank status value.
    const taskNotStarted = filteredStdData.length - done - inProg;
    const unclassified = filteredStdData.length
      - compliant - nonCompliant - partially;
    return {
      done, in_progress: inProg, compliance: compliant, non_compliance: nonCompliant,
      partially,
      task_not_started: taskNotStarted, unclassified_compliance: unclassified,
      total: filteredStdData.length,
    };
  }, [isRowFilterActive, filteredStdData, filteredStdLoading]);

  const activeDashData = filteredKpis
    ? {
        done: filteredKpis.done, in_progress: filteredKpis.in_progress,
        compliance: filteredKpis.compliance, non_compliance: filteredKpis.non_compliance,
        partially: filteredKpis.partially,
        task_not_started: filteredKpis.task_not_started, unclassified_compliance: filteredKpis.unclassified_compliance,
      }
    : dashboardData;
  // The ONE documented denominator for every task-status AND implementation-status
  // KPI card and percentage below — the backend's distinct, tenant/user-scoped
  // action count (or, when a row filter is active, the size of that filtered set).
  // Previously this was done+in_progress only, a narrower population than the
  // compliance_status buckets it was used to compute percentages for, which is
  // exactly why the implementation-status percentages didn't sum to 100%
  // (CN-UAT-17). Do not go back to summing individual buckets here — see
  // `totalTasks` below for why that double-counts.
  const activeTotalTasks = filteredKpis
    ? filteredKpis.total
    : (dashboardData?.total ??
        ((dashboardData?.done || 0) + (dashboardData?.in_progress || 0) +
         (dashboardData?.compliance || 0) + (dashboardData?.non_compliance || 0)));

  // ── Remediation register, for the three remediation widgets ───────────────
  // One fetch of the whole list (the API caps perPage at 500) rather than a
  // request per widget: the cards, the priority donut and the watchlist are all
  // views of the same rows, and the counts have to agree between them.
  //
  // remediationAvailable is false when the fetch is refused — Remediation is
  // permission-gated ('Remediation', ['show']) and a group without it must see no
  // remediation widget at all, not an empty one that reads as "nothing to do".
  const [remediationRows, setRemediationRows] = useState<any[]>([]);
  const [remediationLoading, setRemediationLoading] = useState(true);
  const [remediationAvailable, setRemediationAvailable] = useState(true);
  const [remediationTotalCount, setRemediationTotalCount] = useState(0);

  // Unassigned work (no supervisor / no manager) — null until loaded or on error,
  // in which case the widget renders nothing.
  const [unassigned, setUnassigned] = useState<{
    noSupervisor: number; noManager: number; neither: number;
    byStandard: Array<{ referenceId: string; referenceName: string; noSupervisor: number; noManager: number }>;
  } | null>(null);

  const remediationStats = useMemo(() => {
    const fromTs = dueDateFrom ? new Date(`${dueDateFrom}T00:00:00`).getTime() : null;
    const toTs = dueDateTo ? new Date(`${dueDateTo}T23:59:59`).getTime() : null;

    // The standard filter is applied server-side (referenceId), so only priority
    // and the due-date range are re-applied here. A plan with no due date is kept
    // when no range is picked and dropped when one is — the same rule the
    // compliance side of this filter already uses on its rows.
    const rows = remediationRows.filter((r: any) => {
      if (priorityFilter && String(r.priority || '').toLowerCase() !== priorityFilter) return false;
      if (fromTs === null && toTs === null) return true;
      const ts = r.due_date ? new Date(r.due_date).getTime() : NaN;
      if (Number.isNaN(ts)) return false;
      if (fromTs !== null && ts < fromTs) return false;
      if (toTs !== null && ts > toTs) return false;
      return true;
    });

    const now = Date.now();
    const isOverdue = (r: any) =>
      Boolean(r.due_date) && r.status !== 'Done' && new Date(r.due_date).getTime() < now;

    const byPriority = REMEDIATION_PRIORITIES.map(p => ({
      name: p,
      value: rows.filter((r: any) => String(r.priority || '').toLowerCase() === p).length,
      color: REMEDIATION_PRIORITY_COLORS[p],
    }));

    const done = rows.filter((r: any) => r.status === 'Done').length;
    const inProgress = rows.filter((r: any) => r.status === 'In Progress').length;

    // Watchlist order: overdue first, then by priority, then by the nearest
    // deadline — a plan with no date sorts last rather than first.
    const watchlist = rows
      .filter((r: any) => r.status !== 'Done')
      .sort((a: any, b: any) => {
        const overdueDiff = Number(isOverdue(b)) - Number(isOverdue(a));
        if (overdueDiff !== 0) return overdueDiff;
        const rankDiff =
          (REMEDIATION_PRIORITY_RANK[String(a.priority || '').toLowerCase()] ?? 9) -
          (REMEDIATION_PRIORITY_RANK[String(b.priority || '').toLowerCase()] ?? 9);
        if (rankDiff !== 0) return rankDiff;
        const aTs = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bTs = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return aTs - bTs;
      })
      .slice(0, 6);

    return {
      rows,
      total: rows.length,
      notStarted: rows.filter((r: any) => r.status === 'Not Started').length,
      inProgress,
      done,
      overdue: rows.filter(isOverdue).length,
      urgent: rows.filter((r: any) => ['high', 'critical'].includes(String(r.priority || '').toLowerCase())).length,
      // Average of the per-plan progress the backend already derives from task
      // status — not recomputed from statuses here, so both pages agree.
      avgProgress: rows.length
        ? Math.round(rows.reduce((sum: number, r: any) => sum + (Number(r.progress) || 0), 0) / rows.length)
        : 0,
      byPriority,
      watchlist,
      isOverdue,
    };
  }, [remediationRows, priorityFilter, dueDateFrom, dueDateTo]);

  /**
   * Any remediation widget is worth rendering only when the module answered.
   * A filter that matches nothing keeps them up on purpose — an empty state that
   * says so beats a section that silently disappears mid-filter, taking the
   * priority select with it.
   */
  const showRemediationWidgets =
    modulePermissions.remediation &&
    remediationAvailable && (remediationLoading || remediationTotalCount > 0 || isAnyFilterActive);

  // The standard + due-date fields stay tied to the roles that always had them:
  // both drive the compliance-report refetch. The priority field is remediation-only,
  // so it appears for every role that has remediation data — which is why the card
  // itself can now be shown for a role whose compliance filter is off.
  const showComplianceFilterFields = roleConfig.showComplianceFilter && (complianceForEachStandard as any[]).length > 0;
  const showFilterCard = showComplianceFilterFields || showRemediationWidgets;

  // Unfiltered grand total, used for the filter card's "Showing X of Y tasks"
  // summary. Must be the same distinct-action total as activeTotalTasks/
  // totalTasks below, NOT a sum of the 8 buckets — status and compliance_status
  // are two independent classifications of the SAME rows, so summing both
  // breakdowns double-counts every row (previously produced a grand total ~85%
  // higher than the real task count, and inconsistent with the KPI cards' own
  // percentages — CN-UAT-17).
  const globalTotalTasks =
    dashboardData?.total ??
    ((dashboardData?.done || 0) + (dashboardData?.in_progress || 0) +
     (dashboardData?.compliance || 0) + (dashboardData?.non_compliance || 0));

  // Gauge drill-down modals
  const [complianceGaugeDrillOpen, setComplianceGaugeDrillOpen] = useState(false);
  const [completionGaugeDrillOpen, setCompletionGaugeDrillOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [surveyConductedTime, setSurveyConductedTime] = useState<SurveyData[]>([]);

  const [complianceDataStatus, setComplianceDataStatus] = useState<{ avg_progress: number; quarter: string; year: number }[]>([]);

  const [loading, setLoading] = useState(true);
 
  const [ControlPerCompliance, setControlPerCompliance] = useState<ControlPerCompliance[]>([]);
  const [completionRate, setCompletionRate] = useState<number | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inProgressData, setInProgressData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
    const dashboardRef5 = useRef<HTMLDivElement | null>(null);

  // ===== Compliance Objectives Overview =====
  const [objectives, setObjectives] = useState<ApiObjective[]>([]);
  // ===== Control Mapper Overview =====
  const [controlMapper, setControlMapper] = useState<{ individual?: any[]; grouped?: any[] } | null>(null);
  // ===== Assessment Overview =====
  const [assessment, setAssessment] = useState<any>(null);

  // ── Overview chart data ──────────────────────────────────────────────────
  const statusChartData = useMemo(() => {
    if (!activeDashData) return [];
    return [
      { name: 'Completed',        value: activeDashData.done          || 0, color: '#22C55E', status: 'Done' },
      { name: 'In Progress',      value: activeDashData.in_progress   || 0, color: '#3B82F6', status: 'In Progress' },
      { name: 'Implemented',      value: activeDashData.compliance    || 0, color: '#5a9b82', status: 'Compliance' },
      { name: 'Not Implemented',  value: activeDashData.non_compliance|| 0, color: '#EF4444', status: 'Non Compliance' },
    ].filter(d => d.value > 0);
  }, [activeDashData]);

  // dashboardData.total is a real distinct count from the backend — NOT a sum of
  // done+in_progress+compliance+non_compliance, which double-counts every row that
  // has both a status AND a compliance_status (confirmed against real data: that
  // sum came out ~85% higher than the true task count). Falls back to the old sum
  // only for safety against a stale cached response that predates the total field.
  const totalTasks = useMemo(() =>
    filteredKpis
      ? filteredKpis.total
      : dashboardData?.total ??
        ((dashboardData?.done || 0) + (dashboardData?.in_progress || 0) +
         (dashboardData?.compliance || 0) + (dashboardData?.non_compliance || 0)),
  [filteredKpis, dashboardData]);

  const complianceRate = useMemo(() => {
    const total = (activeDashData?.compliance || 0) + (activeDashData?.non_compliance || 0);
    if (!total) return 0;
    return Math.round(((activeDashData?.compliance || 0) / total) * 100);
  }, [activeDashData]);

  const standardBarData = useMemo(() =>
    (complianceForEachStandard || []).map((s: any) => ({
      id: s.id,
      name: s.reference_name?.length > 14 ? s.reference_name.slice(0, 14) + '…' : s.reference_name,
      fullName: s.reference_name,
      Compliant:    s.compliant    || 0,
      Partial:      s.partial      || 0,
      'Not Compliant': s.nonCompliant || 0,
    })),
  [complianceForEachStandard]);

  const gaugeData = useMemo(() => [{
    name: 'Compliance Rate',
    value: complianceRate,
    fill: complianceRate >= 70 ? '#22C55E' : complianceRate >= 40 ? '#F59E0B' : '#EF4444',
  }], [complianceRate]);

  const workloadBarData = useMemo(() =>
    (usersTasksDashboard as any[])
      .filter((u: any) => u.inProgressTasks > 0)
      .map((u: any) => ({
        name: u.name?.split(' ')[0] || u.username || 'User',
        fullName: u.name || u.username,
        tasks: parseInt(u.inProgressTasks, 10) || 0,
        taskList: u.tasks || [],
        rawUser: u,
      }))
      .sort((a: any, b: any) => b.tasks - a.tasks)
      .slice(0, 10),
  [usersTasksDashboard]);

  const filteredActions = useMemo(() =>
    (complianceActions as any[]).filter((item: any) =>
      !tableSearch ||
      item.supervisor?.name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
      item.control?.name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
      item.status?.toLowerCase().includes(tableSearch.toLowerCase())
    ), [complianceActions, tableSearch]);

  const filteredAssignments = useMemo(() =>
    (assignments as any[]).filter((entry: any) =>
      !tableSearch ||
      entry.user?.name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
      entry.user?.email?.toLowerCase().includes(tableSearch.toLowerCase()) ||
      entry.publicSurvey?.name_en?.toLowerCase().includes(tableSearch.toLowerCase())
    ), [assignments, tableSearch]);

  // Dev timing helper: logs how long each dashboard API call takes so the slow
  // one is easy to spot in the console (e.g. "⏱ dashboard/complianceDashboard: 3200ms").
  const timed = <T,>(label: string, p: Promise<T>): Promise<T> => {
    const t0 = performance.now();
    return p.finally(() => {
      // eslint-disable-next-line no-console
      console.log(`⏱ dashboard/${label}: ${Math.round(performance.now() - t0)}ms`);
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Endpoint GET /objectives is not implemented on this backend (404) — disabled.
        // const data = await getObjectivesFromBackend();
        // if (!cancelled && Array.isArray(data)) setObjectives(data as ApiObjective[]);
        void cancelled;
      } catch {
        // Non-fatal: the section falls back to its empty state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Endpoint GET /control-mapper is not implemented on this backend (404) — disabled.
        // const data = await getControlMapperFromBackend();
        // if (!cancelled) setControlMapper(data);
        void cancelled;
      } catch {
        // Non-fatal: the section falls back to its empty state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Endpoint GET /answers/assessment-dashboard is not implemented on this backend (404) — disabled.
        // const res = await getAssessmentDashboard();
        // const payload = res?.data ?? res;
        // if (!cancelled && payload && typeof payload.overallScore === 'number') {
        //   setAssessment(payload);
        // }
        void cancelled;
      } catch {
        // Non-fatal: the section falls back to its empty state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const objectiveStats = useMemo(() => {
    const list = (Array.isArray(objectives) && objectives.length > 0) ? objectives : SEED_OBJECTIVES;
    const norm = (v: any) => String(v ?? '').trim().toLowerCase();

    const total = list.length;
    const highPriority = list.filter((o) => Number(o.priority_rank) === 1).length;
    const highEffort = list.filter((o) => norm(o.estimated_effort) === 'high').length;
    const frameworks = new Set(
      list.map((o) => norm(o.regulatory_reference)).filter(Boolean),
    ).size;

    // Objectives by priority rank
    const priorityCounts: Record<string, number> = {};
    list.forEach((o) => {
      const rank = Number(o.priority_rank) || 0;
      if (!rank) return;
      priorityCounts[rank] = (priorityCounts[rank] || 0) + 1;
    });
    const priorityData = Object.keys(priorityCounts)
      .sort((a, b) => Number(a) - Number(b))
      .map((rank) => ({
        name: t('Dashboard.priorityRank', { rank }) as string,
        value: priorityCounts[rank],
      }));

    // Objectives by estimated effort (High → Medium → Low)
    const effortOrder = ['high', 'medium', 'low'];
    const effortCounts: Record<string, number> = {};
    list.forEach((o) => {
      const key = norm(o.estimated_effort) || 'unspecified';
      effortCounts[key] = (effortCounts[key] || 0) + 1;
    });
    const effortData = Object.keys(effortCounts)
      .sort((a, b) => {
        const ia = effortOrder.indexOf(a);
        const ib = effortOrder.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map((k) => ({
        name:
          k === 'unspecified'
            ? (t('Dashboard.noDataAvailable') as string)
            : k.charAt(0).toUpperCase() + k.slice(1),
        value: effortCounts[k],
      }));

    return { total, highPriority, highEffort, frameworks, priorityData, effortData };
  }, [objectives, t]);

  // Objectives Timeline — count objectives due per month (suggested_completion_date)
  const objectiveTimeline = useMemo(() => {
    const list = (Array.isArray(objectives) && objectives.length > 0) ? objectives : SEED_OBJECTIVES;
    const months = [t('Jan'), t('Feb'), t('Mar'), t('Apr'), t('May'), t('Jun'), t('Jul'), t('Aug'), t('Sep'), t('Oct'), t('Nov'), t('Dec')];
    const counts: Record<string, number> = {};
    list.forEach((o) => {
      const raw = o.suggested_completion_date;
      if (!raw) return;
      const dt = new Date(raw);
      if (Number.isNaN(dt.getTime())) return;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts)
      .sort()
      .map((k) => {
        const [y, m] = k.split('-');
        return { name: `${months[Number(m) - 1]} ${y}`, value: counts[k] };
      });
  }, [objectives, t]);

  // Objectives by Regulatory Reference — count objectives per framework
  const objectiveRegulation = useMemo(() => {
    const list = (Array.isArray(objectives) && objectives.length > 0) ? objectives : SEED_OBJECTIVES;
    const counts: Record<string, number> = {};
    list.forEach((o) => {
      const ref = String(o.regulatory_reference ?? '').trim();
      if (!ref) return;
      counts[ref] = (counts[ref] || 0) + 1;
    });
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .map((ref) => ({ name: ref, value: counts[ref] }));
  }, [objectives]);

  // Control Mapper Overview — flatten grouped + individual controls into rows and aggregates
  const controlMapperStats = useMemo(() => {
    const hasRealMapper = Boolean(controlMapper?.grouped?.length || controlMapper?.individual?.length);
    const src = hasRealMapper ? controlMapper! : SEED_CONTROL_MAPPER;
    const grouped = Array.isArray(src.grouped) ? src.grouped : [];
    const individual = Array.isArray(src.individual) ? src.individual : [];
    const unassigned = t('Dashboard.unassigned') as string;
    const naLabel = t('Dashboard.na') as string;
    const groupedLabel = t('Dashboard.groupedControls') as string;
    const individualLabel = t('Dashboard.individualControls') as string;

    const rows: Array<{
      code: string; name: string; description: string; objective: string;
      owner: string; mandatoryType: string; source: string; groupName: string;
    }> = [];

    grouped.forEach((g: any) => {
      const owner = g?.suggested_owner_role_department?.name || unassigned;
      (g?.mapped_controls || []).forEach((c: any) => {
        if (!c) return;
        rows.push({
          code: c.code || naLabel,
          name: c.name || naLabel,
          description: c.description || naLabel,
          objective: c.objective || naLabel,
          owner,
          mandatoryType: c.mandatoryType || naLabel,
          source: groupedLabel,
          groupName: g?.name || naLabel,
        });
      });
    });

    individual.forEach((item: any) => {
      const c = item?.control;
      if (!c) return;
      const owner = item?.suggested_owner_role_department?.name || unassigned;
      rows.push({
        code: c.code || naLabel,
        name: c.name || naLabel,
        description: c.description || naLabel,
        objective: c.objective || naLabel,
        owner,
        mandatoryType: c.mandatoryType || naLabel,
        source: individualLabel,
        groupName: naLabel,
      });
    });

    const groupedCount = rows.filter((r) => r.source === groupedLabel).length;
    const individualCount = rows.length - groupedCount;
    const distribution = [
      { name: individualLabel, value: individualCount },
      { name: groupedLabel, value: groupedCount },
    ].filter((d) => d.value > 0);

    const ownerCounts: Record<string, number> = {};
    rows.forEach((r) => {
      ownerCounts[r.owner] = (ownerCounts[r.owner] || 0) + 1;
    });
    const topOwners = Object.keys(ownerCounts)
      .map((o) => ({ name: o, value: ownerCounts[o] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    return { rows, distribution, topOwners, total: rows.length };
  }, [controlMapper, t]);

  // Assessment Overview — overall gauge, per-category score bars, completion donut, radar
  const assessmentStats = useMemo(() => {
    const d = assessment ?? SEED_ASSESSMENT;
    if (!d || typeof d.overallScore !== 'number') return null;
    const categories = Array.isArray(d.categories) ? d.categories : [];

    const categoryScores = categories.map((c: any, i: number) => ({
      name: truncateLabel(c.categoryName, 14),
      fullName: c.categoryName,
      value: clampPct(c.score),
      color: CATEGORY_SCORE_COLORS[i % CATEGORY_SCORE_COLORS.length],
    }));

    const radar = categories.map((c: any) => ({
      category: truncateLabel(c.categoryName, 12),
      fullName: c.categoryName,
      score: clampPct(c.score),
      completion: clampPct((c.answeredQuestions / Math.max(c.totalQuestions, 1)) * 100),
    }));

    const answered = Number(d.answeredQuestions) || 0;
    const total = Number(d.totalQuestions) || 0;
    const unanswered = Math.max(total - answered, 0);
    const completionPct = total > 0 ? (answered / total) * 100 : 0;
    const completionDonut = [
      { name: t('Dashboard.answeredStatus') as string, value: answered, color: ASSESSMENT_SCORE_COLOR },
      { name: t('Dashboard.unansweredStatus') as string, value: unanswered, color: '#D1D5DB' },
    ];

    return {
      overall: clampPct(d.overallScore),
      actualScore: Number(d.actualScore) || 0,
      maxScore: Number(d.maxPossibleScore) || 0,
      answered,
      total,
      completionPct,
      categoryScores,
      radar,
      completionDonut,
    };
  }, [assessment, t]);

  // New state variables for other modals
  const [implementedData, setImplementedData] = useState<any[]>([]);
  const [notImplementedData, setNotImplementedData] = useState<any[]>([]);
  const [completedTasksData, setCompletedTasksData] = useState<any[]>([]);
  // The remaining 3 values of the compliance_status dropdown (viewControl.tsx) —
  // shown via the generic drill-down modal rather than a bespoke one per bucket.
  const [partiallyImplementedData, setPartiallyImplementedData] = useState<any[]>([]);
  // CN-UAT-17 reconciliation buckets — the "everything else" slice of each
  // breakdown so counts/percentages sum to the same documented total instead of
  // silently dropping (implementation side) or omitting (task side) records.
  const [taskNotStartedData, setTaskNotStartedData] = useState<any[]>([]);
  const [unclassifiedComplianceData, setUnclassifiedComplianceData] = useState<any[]>([]);
  const [implementedSearchTerm, setImplementedSearchTerm] = useState('');
  const [notImplementedSearchTerm, setNotImplementedSearchTerm] = useState('');
  const [completedTasksSearchTerm, setCompletedTasksSearchTerm] = useState('');
  const [implementedModalOpen, setImplementedModalOpen] = useState(false);
  const [notImplementedModalOpen, setNotImplementedModalOpen] = useState(false);
  const [completedTasksModalOpen, setCompletedTasksModalOpen] = useState(false);
  const [inProgressModalOpen, setInProgressModalOpen] = useState(false);
  const [iframeRisksUrl, setIframeRisksUrl] = useState("");
  
  // New state variables for survey responses modal
  const [surveyResponsesData, setSurveyResponsesData] = useState<any[]>([]);
  const [surveyResponsesSearchTerm, setSurveyResponsesSearchTerm] = useState('');
  const [surveyResponsesModalOpen, setSurveyResponsesModalOpen] = useState(false);
  
  // New state variables for user participate surveys modal
  const [userParticipateData, setUserParticipateData] = useState<any[]>([]);
  const [userParticipateSearchTerm, setUserParticipateSearchTerm] = useState('');
  const [userParticipateModalOpen, setUserParticipateModalOpen] = useState(false);
  
  // New state variables for total surveys modal
  const [totalSurveysData, setTotalSurveysData] = useState<any[]>([]);
  const [totalSurveysSearchTerm, setTotalSurveysSearchTerm] = useState('');
  const [totalSurveysModalOpen, setTotalSurveysModalOpen] = useState(false);

  useEffect(() => {
    if (chartTab !== 'surveys' || loaded.current.taskRate) return;
    loaded.current.taskRate = true;
    const fetchCompletionRate = async () => {
      try {
        const response = await timed('taskCompletionRate', fetchTaskCompletionRate());
        setCompletionRate(parseFloat(response)); // Convert to number
      } catch (err: any) {
        setError("Failed to fetch task completion rate.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompletionRate();
  }, [chartTab]);


  const fetchComplianceData = async () => {
    try {
      const response: any[] = await timed('numControlPerCompliance', fetchNumControlPerCompliance());
  
      // Transform API response to match the expected format
      const formattedResponse: ControlPerCompliance[] = response.map((item) => ({
        compliance_id: item.id,
        total_controls: item.total_controls,
        compliance_code: item.code, // Rename the key
      }));
  
      setControlPerCompliance(formattedResponse);
    //console.log(formattedResponse);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    if (chartTab !== 'surveys' || loaded.current.complianceProgress) return;
    loaded.current.complianceProgress = true;
    const getComplianceStatus = async () => {
      try {
        const result = await timed('complianceProgressOverTime', getComplianceProgressOverTime());
        setComplianceDataStatus(result);
      
      } catch (err:any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getComplianceStatus();
    fetchComplianceData();
  }, [chartTab]);
  // useEffect(() => {
  //   const getComplianceStatus = async () => {
  //     try {
  //       const result = await fetchComplianceStatus();
  //       setComplianceDataStatus(result);
  //     } catch (err:any) {
  //       setError(err.message);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   getComplianceStatus();
  // }, []);

  const fetchComplianceStatus = async () => {
    return [
      { avg_progress: 98.89, quarter: "Q1", year: 2024 },
      { avg_progress: 0, quarter: "Q2", year: 2024 },
      { avg_progress: 0, quarter: "Q1", year: 2025 },
    ];
  };



  useEffect(() => {
    if (chartTab !== 'surveys' || loaded.current.surveyConducted) return;
    loaded.current.surveyConducted = true;
    const getSurveyConductedTime = async () => {
      try {
        const response = await timed('surveyConductedTime', fetchSurveyConductedTime());
        
        // response: { aggregated: Array, detailed: Object }
        // Use aggregated for chart data
        setSurveyConductedTime(Array.isArray(response?.aggregated) ? response.aggregated : []);
        // If you need detailed data for a table, you can use response.detailed
        // Example: setSurveyDetails(response.detailed);
      } catch (err: any) {
        setError(err.message);
        setSurveyConductedTime([]); // fallback to empty array on error
      } finally {
        setLoading(false);
      }
    };
    getSurveyConductedTime();
  }, [chartTab]);




  useEffect(() => {
    if (chartTab !== 'surveys' || loaded.current.totalResponse) return;
    loaded.current.totalResponse = true;
    const getTotalSurvey = async () => {
      try {
        const response = await timed('totalSurveyResponse', fetchTotalSurveyResponse());
        //console.log(response,"response  fetchTotalSurveyResponse====")
        // API returns {count: 381, data: Array(381)} directly, no success property
        if (response && response.count !== undefined) {
          setTotalResponses(response.count || 0);
          setSurveyResponsesData(response.data || []);
        } else {
          setTotalResponses(0);
          setSurveyResponsesData([]);
        }
      } catch (err:any) {
        setError(err.message);
        setTotalResponses(0);
        setSurveyResponsesData([]);
      } finally {
        setLoading(false);
      }
    };

    getTotalSurvey();
  }, [chartTab]);

  
  useEffect(() => {
    if (chartTab !== 'surveys' || loaded.current.totalCount) return;
    loaded.current.totalCount = true;
    const getTotalCountSurveys = async () => {
      try {
        const data = await timed('totalCountSurveys', fetchTotalCountSurveys());
        // API returns {count: X, data: Array} directly, no success property
        if (data && data.count !== undefined) {
          setSurveyCount(data.count || 0);
          setTotalSurveysData(data.data || []);
        } else {
          setSurveyCount(0);
          setTotalSurveysData([]);
        }
      } catch (err:any) {
        setError(err.message);
        setSurveyCount(0);
        setTotalSurveysData([]);
      } finally {
        setLoading(false);
      }
    };

    getTotalCountSurveys();
  }, [chartTab]);


 

  useEffect(() => {
    if (chartTab !== 'surveys' || loaded.current.participants) return;
    loaded.current.participants = true;
    const fetchData = async () => {
      try {
        const data = await timed('countUsersParticipateSurveys', fetchCountUsersParticipateSurveys());
        //console.log(data,"data fetchCountUsersParticipateSurveys====?")
        // API returns {count: 1, data: Array(3)} directly, no success property
        if (data && data.count !== undefined) {
          setParticipantCount(data.count || 0);
          setUserParticipateData(data.data || []);
        } else {
          setParticipantCount(0);
          setUserParticipateData([]);
        }
      } catch (err:any) {
        setError(err.message);
        setParticipantCount(0);
        setUserParticipateData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chartTab]);



  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleInProgressModal = () => {
    setInProgressModalOpen(!inProgressModalOpen);
  };

  const toggleFullyImplementedModal = () => {
    setImplementedModalOpen(!implementedModalOpen);
  };

  const toggleNotImplementedModal = () => {
    setNotImplementedModalOpen(!notImplementedModalOpen);
  };

  const toggleCompletedTasksModal = () => {
    setCompletedTasksModalOpen(!completedTasksModalOpen);
  };

  const toggleSurveyResponsesModal = () => {
    setSurveyResponsesModalOpen(!surveyResponsesModalOpen);
  };

  const toggleUserParticipateModal = () => {
    setUserParticipateModalOpen(!userParticipateModalOpen);
  };

  const toggleTotalSurveysModal = () => {
    setTotalSurveysModalOpen(!totalSurveysModalOpen);
  };

  const handleInProgressClick = () => {
    //console.log('In Progress clicked!');
    //console.log('inProgressData:', inProgressData);
    //console.log('dashboardData:', dashboardData);
    
    if (inProgressData && inProgressData.length > 0) {
      //console.log('Opening modal with existing data');
      setSearchTerm(''); // Reset search when opening modal
      setInProgressModalOpen(true);
    } else {
      // console.log('No in-progress data available');
      // Try to get data from dashboardData as fallback
      if (dashboardData?.in_progress && Array.isArray(dashboardData.in_progress)) {
        // console.log('Setting data from dashboardData fallback');
        setInProgressData(dashboardData.in_progress);
        setSearchTerm('');
        setInProgressModalOpen(true);
      }
    }
  };

  const handleImplementedClick = () => {
    // console.log('Implemented clicked!');
    // console.log('implementedData:', implementedData);
    
    if (implementedData && implementedData.length > 0) {
      // console.log('Opening fully implemented modal');
      setImplementedSearchTerm(''); // Reset search when opening modal
      setImplementedModalOpen(true);
    } else {
      // console.log('No fully implemented data available');
    }
  };

  const handleNotImplementedClick = () => {
    // console.log('Not Implemented clicked!');
    // console.log('notImplementedData:', notImplementedData);
    
    if (notImplementedData && notImplementedData.length > 0) {
      // console.log('Opening not implemented modal');
      setNotImplementedSearchTerm(''); // Reset search when opening modal
      setNotImplementedModalOpen(true);
    } else {
      // console.log('No not implemented data available');
    }
  };

  const handleCompletedTasksClick = () => {
    // console.log('Completed Tasks clicked!');
    // console.log('completedTasksData:', completedTasksData);
    
    if (completedTasksData && completedTasksData.length > 0) {
      // console.log('Opening completed tasks modal');
      setCompletedTasksSearchTerm(''); // Reset search when opening modal
      setCompletedTasksModalOpen(true);
    } else {
      // console.log('No completed tasks data available');
    }
  };

  // ── Standard-scoped status drill-down ─────────────────────────────────────
  // The KPI cards and status-donut segments already show activeDashData (the
  // selected standard's numbers), but clicking through used to always open the
  // legacy global modals (completedTasksData/inProgressData/etc.) — showing
  // every standard's tasks regardless of what number you just clicked. When a
  // standard is selected, route the click through filteredStdData instead so
  // the drill-down matches what's on screen.
  const [standardStatusDrill, setStandardStatusDrill] = useState<{
    open: boolean; title: string; columns: DrillDownColumn[]; rows: Record<string, any>[];
  }>({ open: false, title: '', columns: [], rows: [] });

  const STANDARD_STATUS_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'control', label: 'Control' },
    { key: 'domain', label: 'Domain' },
    { key: 'status', label: 'Task Status' },
    { key: 'compliance_status', label: 'Compliance Status' },
    { key: 'due_date', label: 'Due Date' },
  ];

  // ── Server-paged task-bucket drill-down ──────────────────────────────────
  // Every KPI card, donut segment and status bucket opens THIS one modal. It
  // replaces four hand-rolled full-list modals that each rendered the entire
  // bucket with no pagination, and it takes the dashboard's own standard and
  // due-date filters with it so the list always matches the number clicked.
  const [taskBucketDrill, setTaskBucketDrill] = useState<{
    open: boolean; title: string; subtitle?: string; bucket: string;
  }>({ open: false, title: '', bucket: 'done' });

  // Dashboard bucket key → the backend's BUCKET_WHERE key.
  const TASK_BUCKET_API: Record<StatusBucket, string> = {
    done: 'done',
    in_progress: 'in_progress',
    compliance: 'fully_implemented',
    non_compliance: 'not_implemented',
    partially: 'partially_implemented',
    task_not_started: 'task_not_started',
    unclassified_compliance: 'unclassified_compliance',
  };

  const fetchTaskBucketPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      const res = await fetchDashboardBucketPage({
        bucket: taskBucketDrill.bucket,
        perPage: pageSize,
        pageNo: page,
        search,
        standardId: selectedStandardId || undefined,
        dueFrom: dueDateFrom || undefined,
        dueTo: dueDateTo || undefined,
        sortBy,
        sortOrder,
      });
      const rows = ((res?.data || []) as any[]).map((r: any) => ({
        control: r.control?.name || '',
        domain: r.domain?.en_name || r.domain?.ar_name || '',
        status: r.status || '',
        compliance_status: r.compliance_status || '',
        due_date: r.due_date ? new Date(r.due_date).toLocaleDateString() : '',
        // Not rendered as columns — carried through so the row can navigate to
        // /compliances/view-control/:controlId/:complianceId/:domainId on click.
        control_id: r.control?.id || '',
        compliance_id: r.compliance_id || '',
        domain_id: r.domain?.id || '',
      }));
      return { rows, totalCount: res?.totalCount || 0, totalPages: res?.totalPages || 1 };
    },
    [taskBucketDrill.bucket, selectedStandardId, dueDateFrom, dueDateTo],
  );

  // ── Survey + workload drill-downs, all server-paged ──────────────────────
  // Each replaces a hand-rolled modal that rendered its whole feed unpaginated.
  const [surveyResponsesDrillOpen, setSurveyResponsesDrillOpen] = useState(false);
  const [participantsDrillOpen, setParticipantsDrillOpen] = useState(false);
  const [surveysDrillOpen, setSurveysDrillOpen] = useState(false);
  const [workloadDrill, setWorkloadDrill] = useState<{ open: boolean; user: any | null }>({ open: false, user: null });

  const SURVEY_RESPONSES_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'user', label: 'User' },
    { key: 'email', label: 'Email' },
    { key: 'survey', label: 'Survey' },
    { key: 'answer', label: 'Answer' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'year', label: 'Year' },
    { key: 'createdAt', label: 'Created' },
  ];

  const PARTICIPANTS_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'user', label: 'User' },
    { key: 'email', label: 'Email' },
    { key: 'username', label: 'Username' },
    { key: 'survey', label: 'Survey' },
    { key: 'createdAt', label: 'Joined' },
  ];

  const SURVEYS_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'name', label: 'Survey' },
    { key: 'description', label: 'Description' },
    { key: 'createdAt', label: 'Created' },
  ];

  const WORKLOAD_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'control', label: 'Control / Task' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'status', label: 'Status' },
  ];

  const fetchSurveyResponsesPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      const res = await fetchTotalSurveyResponsesPage({ perPage: pageSize, pageNo: page, search, sortBy, sortOrder });
      const rows = ((res?.data || []) as any[]).map((r: any) => ({
        user: r.user?.name || '',
        email: r.user?.email || '',
        survey: r.survey?.name_en || r.survey?.name_ar || '',
        answer: r.answer || '',
        quarter: r.quarter || '',
        year: r.year ?? '',
        createdAt: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
      }));
      return { rows, totalCount: res?.count || 0, totalPages: res?.totalPages || 1 };
    },
    [],
  );

  const fetchParticipantsPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      const res = await fetchSurveyParticipantsPage({ perPage: pageSize, pageNo: page, search, sortBy, sortOrder });
      const rows = ((res?.data || []) as any[]).map((r: any) => ({
        user: r.user?.name || '',
        email: r.user?.email || '',
        username: r.user?.username || '',
        survey: r.publicSurvey?.name_en || r.publicSurvey?.name_ar || '',
        createdAt: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
      }));
      return { rows, totalCount: res?.count || 0, totalPages: res?.totalPages || 1 };
    },
    [],
  );

  const fetchSurveysDrillPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      const res = await fetchSurveysPage({ perPage: pageSize, pageNo: page, search, sortBy, sortOrder });
      const rows = ((res?.data || []) as any[]).map((r: any) => ({
        name: r.name_en || r.name_ar || '',
        description: r.description_en || r.description_ar || '',
        createdAt: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
      }));
      return { rows, totalCount: res?.count || 0, totalPages: res?.totalPages || 1 };
    },
    [],
  );

  const fetchWorkloadPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      const functionId = workloadDrill.user?.rawUser?.id || workloadDrill.user?.id;
      if (!functionId) return { rows: [], totalCount: 0, totalPages: 1 };
      const res = await fetchWorkloadTasksPage({ functionId, perPage: pageSize, pageNo: page, search, sortBy, sortOrder });
      const rows = ((res?.data || []) as any[]).map((r: any) => ({
        control: r.control?.name || '',
        due_date: r.due_date ? new Date(r.due_date).toLocaleDateString() : '',
        status: r.status || 'In Progress',
      }));
      return { rows, totalCount: res?.totalCount || 0, totalPages: res?.totalPages || 1 };
    },
    [workloadDrill.user],
  );

  // Every control linked to one standard — the Compliance-by-Standard bar and
  // both gauge breakdowns land here. Server-paged through the same bucket
  // endpoint, filtered by standard with no status restriction.
  const [standardDrill, setStandardDrill] = useState<{ open: boolean; id: string; name: string }>({
    open: false, id: '', name: '',
  });

  const fetchStandardDrillPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      if (!standardDrill.id) return { rows: [], totalCount: 0, totalPages: 1 };
      const res = await fetchDashboardBucketPage({
        bucket: 'all', perPage: pageSize, pageNo: page, search, standardId: standardDrill.id, sortBy, sortOrder,
      });
      const rows = ((res?.data || []) as any[]).map((r: any) => ({
        control: r.control?.name || '',
        domain: r.domain?.en_name || r.domain?.ar_name || '',
        status: r.status || '',
        compliance_status: r.compliance_status || '',
        due_date: r.due_date ? new Date(r.due_date).toLocaleDateString() : '',
        // Not rendered as columns — carried through so the row can navigate to
        // /compliances/view-control/:controlId/:complianceId/:domainId on click.
        control_id: r.control?.id || '',
        compliance_id: r.compliance_id || '',
        domain_id: r.domain?.id || '',
      }));
      return { rows, totalCount: res?.totalCount || 0, totalPages: res?.totalPages || 1 };
    },
    [standardDrill.id],
  );

  const openStandardDrill = (id: string, name: string) => {
    if (!id) return;
    setStandardDrill({ open: true, id, name });
  };

  // The two gauge breakdowns are an aggregate over standardBarData — one row
  // per standard, already computed for the bar chart. There is no per-row feed
  // to page against, so these use the modal's built-in paging over that
  // aggregate; a row opens the server-paged standard drill above.
  const GAUGE_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'standard', label: 'Standard' },
    { key: 'compliant', label: 'Compliant' },
    { key: 'partial', label: 'Partial' },
    { key: 'non_compliant', label: 'Non-Compliant' },
    { key: 'rate', label: 'Rate' },
  ];

  const gaugeDrillRows = useMemo(() => (standardBarData as any[]).map((row: any) => {
    const total = (row.Compliant || 0) + (row.Partial || 0) + (row['Not Compliant'] || 0);
    return {
      id: row.id,
      fullName: row.fullName || row.name,
      standard: row.fullName || row.name,
      compliant: row.Compliant || 0,
      partial: row.Partial || 0,
      non_compliant: row['Not Compliant'] || 0,
      rate: `${total > 0 ? Math.round(((row.Compliant || 0) / total) * 100) : 0}%`,
    };
  }), [standardBarData]);

  /** Opens the shared task drill on one bucket, naming any active filter. */
  const openTaskBucketDrill = (bucket: StatusBucket) => {
    const filterLabel = selectedStandardId
      ? (complianceForEachStandard as any[]).find((st: any) => st.id === selectedStandardId)?.reference_name
        || t('Dashboard.selectedStandardFallback', { defaultValue: 'Selected Standard' })
      : dueDateRangeLabel();
    setTaskBucketDrill({
      open: true,
      bucket: TASK_BUCKET_API[bucket],
      title: STATUS_BUCKET_LABELS[bucket],
      subtitle: filterLabel ? String(filterLabel) : undefined,
    });
  };

  // ── Remediation drill-downs ───────────────────────────────────────────────
  // The remediation cards and the priority donut used to jump straight to
  // /remediation, which throws away the standard/priority/due-date filters the
  // number on the card was counted under — so the page you landed on rarely
  // showed the figure you clicked. They open the shared DrillDownModal now,
  // scoped to exactly that bucket; a row inside still opens the plan's page.
  const [remediationDrill, setRemediationDrill] = useState<{
    open: boolean; title: string; subtitle?: string; bucket: string;
  }>({ open: false, title: '', bucket: 'all' });

  const REMEDIATION_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'name', label: t('Remediation_Page.col_name', { defaultValue: 'Plan' }) as string },
    { key: 'status', label: t('Remediation_Page.col_status', { defaultValue: 'Status' }) as string },
    { key: 'priority', label: t('Remediation_Page.col_priority', { defaultValue: 'Priority' }) as string },
    { key: 'due_date', label: t('Remediation_Page.col_due', { defaultValue: 'Due Date' }) as string },
    { key: 'progress', label: t('Remediation_Page.col_progress', { defaultValue: 'Progress' }) as string },
  ];

  type RemediationBucket =
    | 'all' | 'not_started' | 'in_progress' | 'done' | 'overdue' | 'urgent' | 'watchlist'
    | `priority:${string}`;


  // The remediation drill pages against GET /remediation, carrying the
  // dashboard's own priority and due-date filters so the list keeps matching
  // the number on the card that opened it.
  const fetchRemediationDrillPage = useCallback(
    async ({ page, pageSize, search, sortBy, sortOrder }: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
      // A donut slice arrives as `priority:<value>`. That is a priority filter,
      // not a bucket — send it as one, and let the slice win over whatever the
      // dashboard's own priority filter happens to be set to.
      const slicePriority = remediationDrill.bucket.startsWith('priority:')
        ? remediationDrill.bucket.slice('priority:'.length)
        : '';
      const res: any = await getRemediations({
        bucket: slicePriority ? 'all' : remediationDrill.bucket,
        perPage: pageSize,
        pageNo: page,
        query: search || undefined,
        priority: slicePriority || priorityFilter || undefined,
        dueFrom: dueDateFrom || undefined,
        dueTo: dueDateTo || undefined,
        // Ignored server-side when bucket is 'watchlist' — that one keeps its
        // own fixed overdue/priority/due-date ordering on purpose.
        sortBy,
        sortOrder,
      });
      const now = Date.now();
      const rows = ((res?.data || []) as any[]).map((r: any) => {
        const overdue = Boolean(r.due_date) && r.status !== 'Done' && new Date(r.due_date).getTime() < now;
        return {
          id: r.id,
          name: r.name || '',
          status: r.status || '',
          priority: t(`Remediation_Page.priority_${String(r.priority || '').toLowerCase()}`, {
            defaultValue: r.priority || '',
          }) as string,
          due_date: overdue
            ? `${fmtRemediationDate(r.due_date || null)} · ${t('Remediation_Page.overdue') as string}`
            : fmtRemediationDate(r.due_date || null),
          progress: `${Number(r.progress) || 0}%`,
        };
      });
      return { rows, totalCount: res?.totalCount || 0, totalPages: res?.totalPages || 1 };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remediationDrill.bucket, priorityFilter, dueDateFrom, dueDateTo],
  );

  const openRemediationDrill = (bucket: RemediationBucket, title: string) => {
    // Spell out the dashboard filters the bucket was counted under, so a short
    // list reads as "filtered" rather than "nothing to do".
    const activeFilters = [
      priorityFilter
        ? (t(`Remediation_Page.priority_${priorityFilter}`, { defaultValue: priorityFilter }) as string)
        : null,
      dueDateRangeLabel(),
    ].filter(Boolean) as string[];

    setRemediationDrill({
      open: true,
      title,
      subtitle: activeFilters.length
        ? (t('Dashboard.remediationDrillFiltered', {
            defaultValue: 'Filtered by {{filters}} · click a row to open the plan',
            filters: activeFilters.join(' · '),
          }) as string)
        : (t('Dashboard.remediationDrillHint', {
            defaultValue: 'Click a row to open the plan',
          }) as string),
      bucket,
    });
  };

  // ── "View All" drill-downs for the team-wide activity widgets ─────────────
  // Reuses the already-fetched full lists (complianceActions / assignments,
  // both capped at 200 server-side) — no extra request, just search +
  // pagination over data that's already on the page.
  const [teamTasksDrillOpen, setTeamTasksDrillOpen] = useState(false);
  const TEAM_TASKS_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'task', label: 'Task Name' },
    { key: 'supervisor', label: 'Supervisor Function' },
    { key: 'manager', label: 'Manager Function' },
    { key: 'status', label: 'Status' },
    { key: 'dueDate', label: 'Date & Time' },
  ];
  const teamTasksDrillRows = useMemo(() => (complianceActions as any[]).map((item: any) => ({
    task: item.control?.name || '',
    supervisor: item.supervisor?.name || '',
    manager: item.manager?.name || '',
    status: item.status || '',
    dueDate: item.due_date ? new Date(item.due_date).toLocaleString() : '',
  })), [complianceActions]);

  // True server-side pagination for the "View All" modal — the widget's own
  // 200-row batch above stays as-is; this pages through every qualifying
  // task instead of re-paginating client-side over that single batch.
  const fetchTeamTasksPage = useCallback(async ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) => {
    const res = await fetchComplianceActionsOverview({ perPage: pageSize, pageNo: page, search });
    const rows = ((res?.data || []) as any[]).map((item: any) => ({
      task: item.control?.name || '',
      supervisor: item.supervisor?.name || '',
      manager: item.manager?.name || '',
      status: item.status || '',
      dueDate: item.due_date ? new Date(item.due_date).toLocaleString() : '',
    }));
    return { rows, totalCount: res?.totalCount || 0, totalPages: res?.totalPages || 1 };
  }, []);

  const [surveyAssignmentsDrillOpen, setSurveyAssignmentsDrillOpen] = useState(false);
  const SURVEY_ASSIGNMENTS_DRILL_COLUMNS: DrillDownColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'survey', label: 'Survey' },
    { key: 'functions', label: 'Function(s)' },
  ];
  const surveyAssignmentsDrillRows = useMemo(() => (assignments as any[]).map((entry: any) => ({
    name: entry.user?.name || '',
    email: entry.user?.email || '',
    survey: entry.publicSurvey?.name_en || '',
    functions: (entry.publicSurvey?.publicSurveyFunctions || [])
      .map((pf: any) => pf.Function?.name)
      .filter(Boolean)
      .join(', '),
  })), [assignments]);

  const fetchSurveyAssignmentsPage = useCallback(async ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) => {
    const res = await fetchSurveyAssignmentsOverview({ perPage: pageSize, pageNo: page, search });
    const rows = ((res?.data || []) as any[]).map((entry: any) => ({
      name: entry.user?.name || '',
      email: entry.user?.email || '',
      survey: entry.publicSurvey?.name_en || '',
      functions: (entry.publicSurvey?.publicSurveyFunctions || [])
        .map((pf: any) => pf.Function?.name)
        .filter(Boolean)
        .join(', '),
    }));
    return { rows, totalCount: res?.totalCount || 0, totalPages: res?.totalPages || 1 };
  }, []);

  type StatusBucket = 'done' | 'in_progress' | 'compliance' | 'non_compliance'
    | 'partially'
    | 'task_not_started' | 'unclassified_compliance';

  const STATUS_BUCKET_LABELS: Record<StatusBucket, string> = {
    done: 'Completed', in_progress: 'In Progress', compliance: 'Implemented', non_compliance: 'Not Implemented',
    partially: 'Partially Implemented',
    // CN-UAT-17 reconciliation buckets — see KNOWN_TASK_STATUSES /
    // KNOWN_COMPLIANCE_STATUSES on the backend (getComplianceDashboardData).
    task_not_started: 'Not Started', unclassified_compliance: 'Unclassified',
  };

  const KNOWN_COMPLIANCE_STATUSES = ['Implemented', 'Not Implemented', 'Partially Implemented'];

  // Matches a bucket against the compliance_status dropdown value (viewControl.tsx)
  // for a single row — shared by the standard-filtered and global drill-downs so
  // both classify rows identically.
  const rowMatchesBucket = (bucket: StatusBucket, status: string, complianceStatus: string): boolean => {
    switch (bucket) {
      case 'done': return status === 'Done';
      case 'in_progress': return status === 'In Progress';
      case 'compliance': return complianceStatus === 'Implemented';
      case 'non_compliance': return complianceStatus === 'Not Implemented';
      case 'partially': return complianceStatus === 'Partially Implemented';
      // "Everything else" for each breakdown, so every row lands in exactly one
      // bucket instead of some rows matching none (CN-UAT-17).
      case 'task_not_started': return status !== 'Done' && status !== 'In Progress';
      case 'unclassified_compliance': return !KNOWN_COMPLIANCE_STATUSES.includes(complianceStatus);
    }
  };

  // Global (non-standard-filtered) data source for the 4 new buckets — the
  // legacy 4 buckets each have their own bespoke full-page modal; these reuse
  // the generic DrillDownModal instead of duplicating that per bucket.
  const GLOBAL_BUCKET_DATA: Partial<Record<StatusBucket, any[]>> = {
    partially: partiallyImplementedData,
    task_not_started: taskNotStartedData,
    unclassified_compliance: unclassifiedComplianceData,
  };

  // One path for every bucket now. The old version branched three ways —
  // filteredStdData when a filter was on, a bespoke modal for the four legacy
  // buckets, and in-memory rows for the rest — which is how the legacy modals
  // ended up showing every standard's tasks next to a filtered number, and how
  // an empty bucket silently opened nothing at all.
  const handleStatusBucketClick = (bucket: StatusBucket) => {
    openTaskBucketDrill(bucket);
  };


  const handleSurveyResponsesClick = () => {
    // console.log('Survey Responses clicked!');
    // console.log('surveyResponsesData:', surveyResponsesData);
    
    if (surveyResponsesData && surveyResponsesData.length > 0) {
      // console.log('Opening modal with existing data');
      setSurveyResponsesSearchTerm('');
      setSurveyResponsesDrillOpen(true);
    } else {
      // console.log('No survey responses data available');
      // Fetch data if not available
      fetchSurveyResponsesData();
    }
  };

  // Function to fetch survey responses data
  const fetchSurveyResponsesData = async () => {
    try {
      const response = await timed('totalSurveyResponse', fetchTotalSurveyResponse());
      // API returns {count: 381, data: Array(381)} directly, no success property
      if (response && response.data) {
        setSurveyResponsesData(response.data || []);
        setSurveyResponsesDrillOpen(true);
        setSurveyResponsesSearchTerm('');
      }
    } catch (error) {
      console.error('Error fetching survey responses:', error);
    }
  };

  const handleUserParticipateClick = () => {
    if (userParticipateData && userParticipateData.length > 0) {
      setUserParticipateSearchTerm(''); // Reset search when opening modal
      setParticipantsDrillOpen(true);
    } else {
      // console.log('No user participate data available');
    }
  };

  const handleTotalSurveysClick = () => {
    if (totalSurveysData && totalSurveysData.length > 0) {
      setTotalSurveysSearchTerm(''); // Reset search when opening modal
      setSurveysDrillOpen(true);
    } else {
      // console.log('No total surveys data available');
    }
  };

  // Filter in-progress data based on search term
  const filteredInProgressData = inProgressData.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.control?.name || item.name || '').toLowerCase().includes(searchLower) ||
      (item.control?.description || item.description || '').toLowerCase().includes(searchLower) ||
      (item.control?.id || item.id || '').toLowerCase().includes(searchLower) ||
      (item.domain?.en_name || '').toLowerCase().includes(searchLower)
    );
  });

  // Filter fully implemented data based on search term
  const filteredImplementedData = implementedData.filter(item => {
    const searchLower = implementedSearchTerm.toLowerCase();
    return (
      (item.control?.name || item.name || '').toLowerCase().includes(searchLower) ||
      (item.control?.description || item.description || '').toLowerCase().includes(searchLower) ||
      (item.control?.id || item.id || '').toLowerCase().includes(searchLower) ||
      (item.domain?.en_name || '').toLowerCase().includes(searchLower)
    );
  });

  // Filter not implemented data based on search term
  const filteredNotImplementedData = notImplementedData.filter(item => {
    const searchLower = notImplementedSearchTerm.toLowerCase();
    return (
      (item.control?.name || item.name || '').toLowerCase().includes(searchLower) ||
      (item.control?.description || item.description || '').toLowerCase().includes(searchLower) ||
      (item.control?.id || item.id || '').toLowerCase().includes(searchLower) ||
      (item.domain?.en_name || '').toLowerCase().includes(searchLower)
    );
  });

  // Filter completed tasks data based on search term
  const filteredCompletedTasksData = completedTasksData.filter(item => {
    const searchLower = completedTasksSearchTerm.toLowerCase();
    return (
      (item.control?.name || item.name || '').toLowerCase().includes(searchLower) ||
      (item.control?.description || item.description || '').toLowerCase().includes(searchLower) ||
      (item.control?.id || item.id || '').toLowerCase().includes(searchLower) ||
      (item.domain?.en_name || '').toLowerCase().includes(searchLower)
    );
  });



  // Filter user participate data based on search term
  const filteredUserParticipateData = userParticipateData.filter(item => {
    const searchLower = userParticipateSearchTerm.toLowerCase();
    return (
      (item.user?.name || '').toLowerCase().includes(searchLower) ||
      (item.user?.email || '').toLowerCase().includes(searchLower) ||
      (item.user?.username || '').toLowerCase().includes(searchLower) ||
      (item.publicSurvey?.name_en || '').toLowerCase().includes(searchLower) ||
      (item.publicSurvey?.name_ar || '').toLowerCase().includes(searchLower) ||
      (item.publicSurvey?.description_en || '').toLowerCase().includes(searchLower) ||
      (item.publicSurvey?.description_ar || '').toLowerCase().includes(searchLower)
    );
  });

  // Filter total surveys data based on search term
  const filteredTotalSurveysData = totalSurveysData.filter(item => {
    const searchLower = totalSurveysSearchTerm.toLowerCase();
    return (
      (item.name_en || '').toLowerCase().includes(searchLower) ||
      (item.name_ar || '').toLowerCase().includes(searchLower) ||
      (item.description_en || '').toLowerCase().includes(searchLower) ||
      (item.description_ar || '').toLowerCase().includes(searchLower) ||
      (item.id || '').toLowerCase().includes(searchLower)
    );
  });

  // Filter survey responses data based on search term
  const filteredSurveyResponsesData = surveyResponsesData.filter(item => {
    const searchLower = surveyResponsesSearchTerm.toLowerCase();
    return (
      (item.user?.name || '').toLowerCase().includes(searchLower) ||
      (item.user?.email || '').toLowerCase().includes(searchLower) ||
      (item.survey?.name_en || '').toLowerCase().includes(searchLower) ||
      (item.survey?.name_ar || '').toLowerCase().includes(searchLower) ||
      (item.answer || '').toLowerCase().includes(searchLower) ||
      (item.quarter || '').toLowerCase().includes(searchLower) ||
      (item.year?.toString() || '').includes(searchLower)
    );
  });

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const response = await timed('complianceDashboard', getComplianceDashboard());
     
      // console.log(response,"in progress=========")
      if (response.success) {
        const counts = response.data?.counts || {};
        const payload = response.data?.data || {};
        setDashboardData(counts);

        // Set the detailed data for each status (arrays are under response.data.data)
        // console.log('Setting data from API response:');
        // console.log('payload.done:', payload.done);
        // console.log('payload.fully_implemented:', payload.fully_implemented);
        // console.log('payload.in_progress:', payload.in_progress);
        // console.log('payload.not_implemented:', payload.not_implemented);
        
        setCompletedTasksData(payload.done || []);
        setImplementedData(payload.fully_implemented || []);
        setInProgressData(payload.in_progress || []);
        setNotImplementedData(payload.not_implemented || []);
        setPartiallyImplementedData(payload.partially_implemented || []);
        setTaskNotStartedData(payload.task_not_started || []);
        setUnclassifiedComplianceData(payload.unclassified_compliance || []);
        
        // Debug logging
        // console.log('Data populated:', {
        //  done: payload.done?.length || 0,
        //  fully_implemented: payload.fully_implemented?.length || 0,
        //  in_progress: payload.in_progress?.length || 0,
      //    not_implemented: payload.not_implemented?.length || 0
       // });

        setComplianceStatus([{
          'Compliance': counts?.compliance,
          'Partial': counts?.partially,
          'NotCompliant': counts?.non_compliance,
          'Null': counts?.nullCompliance + counts?.not_started
        }]);

     
      
    
const monthlyTasksWithMonthNames = Array.isArray(response?.data?.monthlyTasks)
  ? response.data.monthlyTasks.map((task: any, index: number) => {
      const monthNames = [t("Jan"), t("Feb"), t("Mar"), t("Apr"), t("May"), t("Jun"), t("Jul"), t("Aug"), t("Sep"), t("Oct"), t("Nov"), t("Dec")];
      return { name: monthNames[index], noTasks: task };
    })
  : [];


        setMonthlyTasks(monthlyTasksWithMonthNames);
        
      } else {
        setDashboardData(null);
      }

    } catch (error) {
      //console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchComplianceForEachStandardDashboard = async () => {
    try {
      const data = await timed('complianceForEachStandardDashboard', getComplianceForEachStandardDashboard());
      if (data.success) {
        const formattedData = data.data ? Object.keys(data.data).map((key: string) => ({
          id: key,
          reference_name: data.data[key].reference_name,
          compliant: data.data[key].compliance,
          nonCompliant: data.data[key].non_compliance,
          partial: data.data[key].partially
        })) : [];
        
        setComplianceForEachStandard(formattedData);
      }
      
    } catch (error) {
      //console.error("Error fetching compliance for each standard dashboard:", error);
    }
  }

  const fetchUsersTasksDashboard = async () => {
    try {
      const data = await getUsersTasksDashboard();
      if (data.success) {
        setUsersTasksDashboard(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (error) {
      // non-critical — workload chart just stays empty
    }
  };

  useEffect(() => {
    if (chartTab !== 'overview' || loaded.current.dashboard) return;
    loaded.current.dashboard = true;
    fetchDashboardData();
    fetchComplianceForEachStandardDashboard();
    fetchUsersTasksDashboard();
  }, [chartTab]);


 
  useEffect(() => {
    if (chartTab !== 'overview' || loaded.current.assignments) return;
    loaded.current.assignments = true;
    const getAssignments = async () => {
      try {
        const data = await timed('recentAssignments', fetchRecentAssignments());
      //console.log(data);
        setAssignments(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Failed to fetch assignments.");
      }
    };

    getAssignments();
  }, [chartTab]);

  useEffect(() => {
    if (chartTab !== 'overview' || loaded.current.myFunctionSurveys) return;
    loaded.current.myFunctionSurveys = true;
    const getMyFunctionSurveys = async () => {
      setMyFunctionSurveysLoading(true);
      try {
        const functionIds = await timed('myFunctionIds', getMyFunctionIds());
        if (!functionIds.length) { setMyFunctionSurveys([]); return; }
        const data = await timed('surveysForFunctions', fetchSurveysForFunctions(functionIds, 8));
        setMyFunctionSurveys(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error('Failed to fetch surveys for my function(s).');
        setMyFunctionSurveys([]);
      } finally {
        setMyFunctionSurveysLoading(false);
      }
    };

    getMyFunctionSurveys();
  }, [chartTab]);


  // ── Remediation register for the remediation widgets ──────────────────────
  // Refetched when the standard changes because referenceId is a server-side
  // filter; priority and the due-date range are applied to these rows in
  // remediationStats, so changing either costs no request.
  useEffect(() => {
    if (chartTab !== 'overview') return;
    let cancelled = false;
    setRemediationLoading(true);
    timed('remediations', getRemediations({
      perPage: REMEDIATION_FETCH_LIMIT,
      pageNo: 1,
      referenceId: selectedStandardId || undefined,
    }))
      .then((res: any) => {
        if (cancelled) return;
        setRemediationRows(Array.isArray(res?.data) ? res.data : []);
        setRemediationTotalCount(Number(res?.totalCount) || (Array.isArray(res?.data) ? res.data.length : 0));
        setRemediationAvailable(true);
      })
      .catch(() => {
        // Includes the 403 a group without Remediation.show gets — the widgets
        // hide rather than showing zeros that would read as "nothing to remediate".
        if (cancelled) return;
        setRemediationRows([]);
        setRemediationTotalCount(0);
        setRemediationAvailable(false);
      })
      .finally(() => { if (!cancelled) setRemediationLoading(false); });
    return () => { cancelled = true; };
  }, [chartTab, selectedStandardId]);

  // ── Unassigned work — supervisor / leader / super_admin only ──────────────
  useEffect(() => {
    if (chartTab !== 'overview' || !widgetAllowedForRole('unassigned-work', dashboardRole)) return;
    let cancelled = false;
    timed('unassigned', getUnassignedDashboard())
      .then((res: any) => {
        if (cancelled) return;
        const d = res?.success ? res?.data : null;
        setUnassigned(d ? {
          noSupervisor: Number(d.noSupervisor) || 0,
          noManager: Number(d.noManager) || 0,
          neither: Number(d.neither) || 0,
          byStandard: Array.isArray(d.byStandard) ? d.byStandard : [],
        } : null);
      })
      .catch(() => { if (!cancelled) setUnassigned(null); });
    return () => { cancelled = true; };
  }, [chartTab, dashboardRole]);

  useEffect(() => {
    if (chartTab !== 'overview' || loaded.current.complianceActions) return;
    loaded.current.complianceActions = true;
    const getComplianceActions = async () => {
      try {
        const result = await timed('complianceActions', fetchComplianceActions());
        setComplianceActions(Array.isArray(result?.data) ? result.data : []);
      } catch (error) {
        console.error("Failed to fetch assignments.");
      }
    };
    getComplianceActions();
  }, [chartTab]);

 


// Disabled with the Superset embed effect below — its only caller. Kept for reference so
// re-enabling the embed is a matter of uncommenting both.
// const getToken5 = async () => {
//     const data = await getGuestToken5();
//
//     return data
//
//
// }
/*useEffect(() => {
  if (activeTab === "Dashboards") {
     const embed = async () => {
          if (!dashboardRef5?.current) {
            console.error('Dashboard ref is not available');
            return;
          }
      
          // استخدم await هنا لأن getToken3() هي دالة async
          const token1 = await getToken5();
          
          if (!token1) {
            return
          }
         if (dashboardRef5?.current && token1 != null) {
          await embedDashboard({
            id: process.env.REACT_APP_COM_DASH_ID ||'',
            supersetDomain: process.env.REACT_APP_SUPERSET_URL || '',
               mountPoint: dashboardRef5.current,
               fetchGuestToken: () => Promise.resolve(token1), // Fix: Return the token directly
               dashboardUiConfig: {
                   hideTitle: true,
                   hideChartControls: false,
                   hideTab: false,
                   filters: {
                       visible: true,
                       expanded: false,
                   },
               },
           });
 
             // Access the iframe element after embedding
             const iframe = dashboardRef5.current.querySelector('iframe');
             if (iframe) {
                 iframe.style.height = '100%';
                 iframe.style.width = '100%';
             }
         }
     };
     embed();
     
  }
}, [activeTab]);*/

  // ── Standard / due-date-range filter fetch (for CISO / super_admin / supervisor) ──
  // The backend report endpoint only accepts a single upper-bound `date` param, so
  // "to" goes to the server and "from" is applied client-side on the returned rows —
  // that still gives a real from/to range without needing a backend change.
  useEffect(() => {
    if (!isRowFilterActive) { setFilteredStdData([]); return; }
    setFilteredStdLoading(true);
    getReport(selectedStandardId || null, null, null, null, null, dueDateTo || null)
      .then((res: any) => {
        let rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (dueDateFrom) {
          const fromTs = new Date(dueDateFrom).getTime();
          rows = rows.filter((r: any) => {
            if (!r.due_date) return false;
            const ts = new Date(r.due_date).getTime();
            return !isNaN(ts) && ts >= fromTs;
          });
        }
        setFilteredStdData(rows);
        setFilteredStdLoading(false);
      })
      .catch(() => { setFilteredStdData([]); setFilteredStdLoading(false); });
  }, [selectedStandardId, dueDateFrom, dueDateTo, isRowFilterActive]);

  // ── Custom chart data normalizer ─────────────────────────────────────────
  const normalizeCustomData = (source: CustomDataSource): any[] => {
    switch (source) {
      case 'task-status':
        return statusChartData.map(d => ({ name: d.name, value: d.value, color: d.color }));
      case 'per-standard':
        return standardBarData.map((d: any) => ({
          name: d.name, fullName: d.fullName,
          value: (d.Compliant || 0) + (d.Partial || 0) + (d['Not Compliant'] || 0),
        }));
      case 'workload':
        return workloadBarData.map(d => ({ name: d.name, fullName: d.fullName, value: d.tasks }));
      case 'actions': {
        const cnt: Record<string, number> = {};
        (complianceActions as any[]).forEach((a: any) => {
          const s = a.status || 'Unknown';
          cnt[s] = (cnt[s] || 0) + 1;
        });
        return Object.entries(cnt).map(([name, value]) => ({ name, value }));
      }
      default: return [];
    }
  };

  // ── Custom chart drill-down ───────────────────────────────────────────────
  const [customDrill, setCustomDrill] = useState<{
    open: boolean; loading: boolean; title: string; subtitle: string;
    columns: DrillDownColumn[]; rows: Record<string, any>[];
  }>({ open: false, loading: false, title: '', subtitle: '', columns: [], rows: [] });

  const TASK_STATUS_COLUMNS: DrillDownColumn[] = [
    { key: 'name',        label: 'Control Name' },
    { key: 'description', label: 'Description' },
    { key: 'status',      label: 'Status' },
    { key: 'compliance_status', label: 'Compliance Status' },
    { key: 'domain',      label: 'Domain' },
  ];
  const toTaskRow = (item: any) => ({
    name: item.control?.name || item.name || '',
    description: item.control?.description || item.description || '',
    status: item.status || '',
    compliance_status: item.compliance_status || '',
    domain: item.domain?.en_name || '',
  });

  const handleCustomChartDrill = (cc: CustomChart, bucketName: string) => {
    switch (cc.dataSource) {
      case 'task-status': {
        // Routed through the shared server-paged bucket drill, so a custom chart
        // and a KPI card clicked on the same status open the same, consistently
        // filtered list instead of two separately-derived ones.
        const bucket = statusChartData.find(d => d.name === bucketName);
        const STATUS_TO_BUCKET: Record<string, StatusBucket> = {
          'Done': 'done', 'In Progress': 'in_progress',
          'Compliance': 'compliance', 'Non Compliance': 'non_compliance',
        };
        const statusBucket = STATUS_TO_BUCKET[bucket?.status ?? ''];
        if (statusBucket) openTaskBucketDrill(statusBucket);
        break;
      }

      case 'per-standard': {
        // Reuses the server-paged standard drill rather than pulling the whole
        // report into a client-paginated modal.
        const std = standardBarData.find((s: any) => s.name === bucketName || s.fullName === bucketName);
        if (!std?.id) return;
        openStandardDrill(std.id, std.fullName || bucketName);
        break;
      }
      case 'workload': {
        const user = workloadBarData.find((u: any) => u.name === bucketName || u.fullName === bucketName);
        const rows = ((user?.taskList || []) as any[]).map((t: any) => ({
          name: t.controlName || '', dueDate: t.dueDate ? new Date(t.dueDate).toLocaleDateString() : '', status: 'In Progress',
        }));
        setCustomDrill({
          open: true, loading: false, title: `${cc.label} — ${user?.fullName || bucketName}`, subtitle: '',
          columns: [
            { key: 'name', label: 'Control / Task' },
            { key: 'dueDate', label: 'Due Date' },
            { key: 'status', label: 'Status' },
          ],
          rows,
        });
        break;
      }
      case 'actions': {
        const rows = (complianceActions as any[])
          .filter((a: any) => (a.status || 'Unknown') === bucketName)
          .map((a: any) => ({
            name:    a.control?.name || '',
            manager: a.manager?.name || '',
            status:  a.status || '',
            dueDate: a.due_date ? new Date(a.due_date).toLocaleDateString() : '',
          }));
        setCustomDrill({
          open: true, loading: false, title: `${cc.label} — ${bucketName}`, subtitle: '',
          columns: [
            { key: 'name', label: 'Control' },
            { key: 'manager', label: 'Manager' },
            { key: 'status', label: 'Status' },
            { key: 'dueDate', label: 'Due Date' },
          ],
          rows,
        });
        break;
      }
    }
  };

  const renderCustomChart = (cc: CustomChart): React.ReactNode => {
    const data = normalizeCustomData(cc.dataSource);
    const COLORS = ['#5a9b82', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6'];
    const onSliceClick = (name?: string) => { if (name) handleCustomChartDrill(cc, name); };
    return (
      <div className="card h-100 p-3 dash-chart-card" style={{ borderRadius: 10, minHeight: 220, position: 'relative' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{cc.label}</div>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
          {cc.dataSource.replace('-', ' ')} · {cc.chartType.replace('-', ' ')}
        </div>
        {data.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: 160, color: '#9CA3AF', fontSize: 13 }}>No data</div>
        ) : cc.chartType === 'donut' ? (
          <RcResponsiveContainer width="100%" height={170}>
            <RcPieChart>
              <RcPie data={data} dataKey="value" cx="50%" cy="50%" innerRadius="45%" outerRadius="75%"
                onClick={(entry: any) => onSliceClick(entry?.name)} style={{ cursor: 'pointer' }}>
                {data.map((d: any, i: number) => <RcCell key={i} fill={d.color ?? COLORS[i % COLORS.length]} stroke="none"/>)}
              </RcPie>
              <RcTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
              <RcLegend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }}/>
            </RcPieChart>
          </RcResponsiveContainer>
        ) : cc.chartType === 'bar-h' ? (
          <RcResponsiveContainer width="100%" height={Math.max(160, data.length * 28)}>
            <RcBarChart data={data} layout="vertical" margin={{ top: 4, right: 30, left: 70, bottom: 4 }}
              onClick={(d: any) => onSliceClick(d?.activePayload?.[0]?.payload?.name)} style={{ cursor: 'pointer' }}>
              <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false}/>
              <RcXAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false}/>
              <RcYAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} width={65}/>
              <RcTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
              <RcBar dataKey="value" fill="#5a9b82" radius={[0, 4, 4, 0]} maxBarSize={22}
                label={{ position: 'right', fontSize: 10, fill: '#6B7280', formatter: (v: any) => v }}/>
            </RcBarChart>
          </RcResponsiveContainer>
        ) : cc.chartType === 'bar-v' ? (
          <RcResponsiveContainer width="100%" height={170}>
            <RcBarChart data={data} margin={{ top: 4, right: 8, left: -10, bottom: 20 }}
              onClick={(d: any) => onSliceClick(d?.activePayload?.[0]?.payload?.name)} style={{ cursor: 'pointer' }}>
              <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false}/>
              <RcXAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} angle={-25} textAnchor="end" interval={0}/>
              <RcYAxis tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false}/>
              <RcTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
              <RcBar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={32}>
                {data.map((d: any, i: number) => <RcCell key={i} fill={d.color ?? COLORS[i % COLORS.length]}/>)}
              </RcBar>
            </RcBarChart>
          </RcResponsiveContainer>
        ) : (
          <div className="dash-table-scroll" style={{ maxHeight: 180 }}>
            <table className="table table-sm mb-0 dash-table dash-table--auto dash-table--flat">
              <thead>
                <tr>
                  <th style={{ fontSize: 11, padding: '5px 10px', color: '#6B7280' }}>Name</th>
                  <th style={{ fontSize: 11, padding: '5px 10px', color: '#6B7280', textAlign: 'end' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d: any, i: number) => (
                  <tr key={i} style={{ cursor: 'pointer' }} onClick={() => onSliceClick(d.name || d.fullName)}>
                    <td style={{ fontSize: 12, padding: '6px 10px', color: '#374151' }}><div className="dash-clamp-2" title={d.name || d.fullName || ''}>{d.name || d.fullName || '—'}</div></td>
                    <td className="dash-col-nowrap" style={{ fontSize: 12, padding: '6px 10px', fontWeight: 600, color: '#111827', textAlign: 'end' }}>{d.value ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };
  // ── end custom chart helpers ──────────────────────────────────────────────

  if (dashboardPermissionLoading) {
    return (
      <div className="loading-screen"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
        <div><LoadingScreen/></div>
      </div>
    );
  }

  if (dashboardNotAllowed) {
    return (
      <div className="root">
        <div className="container_info" style={{ padding: '40px 20px', backgroundColor: 'var(--widget-bg)' }}>
          <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
            <h5 style={{ width: '100%' }} className="text-center">
              {t('permissions.not_allowed') || "You don't have permission to see this page"}
            </h5>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-screen"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
        <div><LoadingScreen/></div>
      </div>
    );
  }




  return (
    <div className="root dashboard">
     
        <>
          {/* Page breadcrumb + title/description come from the GLOBAL header
              (AppHeaderBreadcrumbs + AppPageHeading in Layout/main.tsx) so the
              dashboard matches every other page. Only page-specific content
              (tabs + widgets) lives here. */}
<div   >

          <div className="dash-tabs" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className={`dash-tab ${chartTab === 'overview' ? 'active' : ''}`} onClick={() => setChartTab('overview')}>{t('Dashboard.Overview') || 'Overview'}</button>
            </div>
            {chartTab === 'overview' && (
              <button
                onClick={() => setCustomizerOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                  borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: '#F0FDF4', border: '1px solid #5a9b82', color: '#5a9b82',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#5a9b82'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F0FDF4'; e.currentTarget.style.color = '#5a9b82'; }}>
                ⚙ Customize
              </button>
            )}
          </div>

          {/* Dashboard Customizer slide-in panel */}
          {customizerOpen && (
            <DashboardCustomizer
              widgetDefs={roleWidgetDefs}
              widgetOrder={widgetOrder}
              widgetWidths={widgetWidths}
              hiddenWidgets={hiddenWidgets}
              customCharts={customCharts}
              dynamicCharts={dynamicCharts}
              isAdmin={isAdmin}
              onReorder={handleReorder}
              onWidthChange={handleWidthChange}
              onToggleHide={handleToggleHide}
              onAddCustom={handleAddCustom}
              onRemoveCustom={handleRemoveCustom}
              onRemoveDynamic={handleRemoveDynamic}
              onOpenDynamicBuilder={() => setDynamicBuilderOpen(true)}
              onReset={handleResetWidgets}
              onClose={() => setCustomizerOpen(false)}
            />
          )}

          {/* Dynamic chart builder modal */}
          <DynamicChartBuilder
            isOpen={dynamicBuilderOpen}
            onClose={() => setDynamicBuilderOpen(false)}
            onAdd={handleAddDynamic}
          />

          {chartTab === 'overview' && (<>
            {/* ── Modals (render before flex grid; they are portals so DOM position doesn't matter) ── */}
            {/* Compliance by Standard drill-down modal */}
{/* Workload user drill-down modal */}
{/* ── Compliance Rate drill-down modal — standards breakdown ── */}
{/* ── Task Completion drill-down modal — all tasks by status ── */}
{/* ── Standard / date-range / priority filter card ── */}
            {showFilterCard && (
              <div className="dash-filter-card">
                <div className="dash-filter-card-row">
                  <div className="dash-filter-card-title">
                    <FaFilter size={13} />
                    {t('Dashboard.filtersLabel', { defaultValue: 'Filters' })}
                    <span
                      className="dash-filter-info"
                      title={t('Dashboard.filtersInfoTooltip', { defaultValue: 'Filter dashboard metrics by compliance standard, due date and remediation priority' }) as string}
                    >
                      <FaInfoCircle size={12} />
                    </span>
                  </div>

                  {showComplianceFilterFields && modulePermissions.standards && (
                    <div className="dash-filter-field">
                      <label className="dash-filter-field-label" htmlFor="dash-filter-standard">
                        {t('Dashboard.standardFieldLabel', { defaultValue: 'Standard' })}
                      </label>
                      <select
                        id="dash-filter-standard"
                        className="dash-filter-input"
                        aria-label={t('Dashboard.filterByStandardLabel', { defaultValue: 'Filter by standard:' }) as string}
                        value={draftStandardId}
                        onChange={e => setDraftStandardId(e.target.value)}
                      >
                        <option value="">{t('Dashboard.allStandardsOption', { defaultValue: 'All standards' })}</option>
                        {(complianceForEachStandard as any[]).map((s: any) => (
                          <option key={s.id} value={s.id}>{s.reference_name || s.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {showComplianceFilterFields && (
                    <div className="dash-filter-field">
                      <label className="dash-filter-field-label">
                        {t('Dashboard.dateRangeFieldLabel', { defaultValue: 'Date range' })}
                      </label>
                      <div className="dash-filter-daterange">
                        <input
                          type="date"
                          className="dash-filter-input dash-filter-input--date"
                          aria-label={t('Dashboard.dateFromAriaLabel', { defaultValue: 'From date' }) as string}
                          value={draftDueDateFrom}
                          max={draftDueDateTo || undefined}
                          onChange={e => setDraftDueDateFrom(e.target.value)}
                        />
                        <span className="dash-filter-daterange-sep">–</span>
                        <input
                          type="date"
                          className="dash-filter-input dash-filter-input--date"
                          aria-label={t('Dashboard.dateToAriaLabel', { defaultValue: 'To date' }) as string}
                          value={draftDueDateTo}
                          min={draftDueDateFrom || undefined}
                          onChange={e => setDraftDueDateTo(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {showRemediationWidgets && (
                    <div className="dash-filter-field">
                      <label className="dash-filter-field-label" htmlFor="dash-filter-priority">
                        {t('Dashboard.priorityFieldLabel', { defaultValue: 'Remediation Priority' })}
                        <span
                          className="dash-filter-info"
                          title={t('Dashboard.priorityFieldTooltip', { defaultValue: 'Applies to the remediation widgets only — not the compliance cards above' }) as string}
                        >
                          <FaInfoCircle size={11} />
                        </span>
                      </label>
                      <select
                        id="dash-filter-priority"
                        className="dash-filter-input"
                        aria-label={t('Dashboard.filterByPriorityLabel', { defaultValue: 'Filter by remediation priority:' }) as string}
                        value={draftPriority}
                        onChange={e => setDraftPriority(e.target.value)}
                      >
                        <option value="">{t('Dashboard.allPrioritiesOption', { defaultValue: 'All priorities' })}</option>
                        {REMEDIATION_PRIORITIES.map(p => (
                          <option key={p} value={p}>{t(`Remediation_Page.priority_${p}`, { defaultValue: p })}</option>
                        ))}
                      </select>
                      <span className="dash-filter-field-hint">
                        {t('Dashboard.priorityFieldHint', { defaultValue: 'Remediation widgets only' })}
                      </span>
                    </div>
                  )}

                  <div className="dash-filter-card-spacer" />

                  <div className="dash-filter-summary">
                    {filteredStdLoading ? (
                      <span className="dash-filter-loading"><FaSpinner /> {t('common.loading', { defaultValue: 'Loading…' })}</span>
                    ) : isRowFilterActive && filteredKpis ? (
                      <span>{t('Dashboard.showingTasksSummary', { defaultValue: 'Showing {{filtered}} of {{total}} tasks', filtered: filteredKpis.total, total: globalTotalTasks })}</span>
                    ) : priorityFilter ? (
                      <span>{t('Dashboard.showingRemediationsSummary', { defaultValue: 'Showing {{filtered}} of {{total}} remediations', filtered: remediationStats.total, total: remediationRows.length })}</span>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className="dash-filter-clearall-btn"
                    disabled={!isAnyFilterActive && !filtersDirty}
                    onClick={clearAllFilters}
                  >
                    <FaTimes size={11} />
                    {t('common.clearFilters', { defaultValue: 'Clear filters' })}
                  </button>
                  <button
                    type="button"
                    className="dash-filter-apply-btn"
                    disabled={!filtersDirty}
                    onClick={applyDraftFilters}
                  >
                    {t('Dashboard.applyFiltersBtn', { defaultValue: 'Apply filters' })}
                  </button>
                </div>
              </div>
            )}
            {!roleConfig.showComplianceFilter && (
              <div style={{
                display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
                padding:'10px 16px', borderRadius:10, marginBottom:14,
                background:roleConfig.bgColor, border:`1px solid ${roleConfig.color}30`,
              }}>
                <div style={{fontSize:11, color:'#9CA3AF'}}>
                  {tokenName && (
                    <span>
                      {t('Dashboard.welcomeLabel', { defaultValue: 'Welcome,' })}{' '}
                      <strong style={{color:'#374151'}}>{tokenName}</strong>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── Ordered flex widget grid ── */}
            <div className="dash-widget-grid" style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'flex-start', marginBottom:12}}>

              {/* kpi-cards — full row of 4 stat cards */}
              {isVisible('kpi-cards') && modulePermissions.complianceTasks && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('kpi-cards',12)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('kpi-cards')}}>
                  {isRowFilterActive && filteredKpis && (
                    <div style={{marginBottom:8, padding:'5px 12px', borderRadius:8, background:'#EFF6FF', border:'1px solid #3B82F630', fontSize:11, color:'#1D4ED8', fontWeight:500, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                      🎯 {t('Dashboard.showingDataForLabel', { defaultValue: 'Showing data for:' })}{' '}
                      {selectedStandardId && (
                        <strong>{(complianceForEachStandard as any[]).find((s: any) => s.id === selectedStandardId)?.reference_name || t('Dashboard.selectedStandardFallback', { defaultValue: 'Selected Standard' })}</strong>
                      )}
                      {selectedStandardId && (dueDateFrom || dueDateTo) && <span>·</span>}
                      {(dueDateFrom || dueDateTo) && <strong>{dueDateRangeLabel()}</strong>}
                      <span style={{color:'#9CA3AF', marginLeft:4}}>{t('Dashboard.controlsCountParens', { defaultValue: '({{count}} controls)', count: filteredKpis.total })}</span>
                    </div>
                  )}
                  <Row className="g-3">
                    {[
                      { label: t('Dashboard.Completed_Tasks'),   value: activeDashData?.done,            color: '#22C55E', bgCls: 'bg-success', icon: <FaCheckCircle size={18} className="text-white"/>,      onClick: () => handleStatusBucketClick('done') },
                      { label: t('Dashboard.In_Progress'),        value: activeDashData?.in_progress,     color: '#3B82F6', bgCls: 'bg-primary', icon: <FaExclamationCircle size={18} className="text-white"/>, onClick: () => handleStatusBucketClick('in_progress') },
                      // "Everything else" of the task-status breakdown (status isn't
                      // Done or In Progress) — without this card, done+in_progress was
                      // a narrower population than the implementation-status cards
                      // below, which is why the two totals didn't reconcile (CN-UAT-17).
                      { label: t('Dashboard.Task_Not_Started'),  value: activeDashData?.task_not_started, color: '#9CA3AF', bgCls: '', bgStyle:{backgroundColor:'#9CA3AF'}, icon: <FaRegCircle size={18} className="text-white"/>,        onClick: () => handleStatusBucketClick('task_not_started') },
                      { label: t('Dashboard.Implemented'), value: activeDashData?.compliance,      color: '#5a9b82', bgCls: '',           icon: <RiProgress2Line size={18} className="text-white"/>,       onClick: () => handleStatusBucketClick('compliance'), bgStyle:{backgroundColor:'#5a9b82'} },
                      { label: t('Dashboard.Not_Implemented'),   value: activeDashData?.non_compliance,  color: '#EF4444', bgCls: 'bg-danger',  icon: <FaExclamationTriangle size={18} className="text-white"/>, onClick: () => handleStatusBucketClick('non_compliance') },
                      // The remaining value of the same compliance_status dropdown
                      // (viewControl.tsx) — color matches that page's status pill.
                      { label: t('Dashboard.Partially_Implemented'), value: activeDashData?.partially,            color: '#F3903F', bgCls: '', bgStyle:{backgroundColor:'#F3903F'}, icon: <FaRegCircle size={18} className="text-white"/>,       onClick: () => handleStatusBucketClick('partially') },
                      // "Everything else" of the implementation-status breakdown
                      // (compliance_status is blank/null/unrecognized) — surfaced
                      // explicitly instead of being silently dropped from the total
                      // (previously left the implementation cards short by exactly
                      // this many records — CN-UAT-17).
                      //
                      // Hidden when it is empty, which is the normal state: once
                      // every row holds a status the cards recognise, a permanent
                      // "Unclassified 0" is a question with no answer. It comes
                      // back on its own the moment a row does not fit, so nothing
                      // is silently dropped from the total - which is the whole
                      // reason the card exists.
                      ...((activeDashData?.unclassified_compliance || 0) > 0
                        ? [{ label: t('Dashboard.Unclassified_Implementation'), value: activeDashData?.unclassified_compliance, color: '#6B7280', bgCls: '', bgStyle:{backgroundColor:'#6B7280'}, icon: <FaBan size={18} className="text-white"/>, onClick: () => handleStatusBucketClick('unclassified_compliance') }]
                        : []),
                    ].map((card, i) => {
                      const pct = activeTotalTasks > 0 ? Math.round(((card.value || 0) / activeTotalTasks) * 100) : 0;
                      return (
                        <Col key={i} xs={12} sm={6} xl={3} className="dash-col">
                          <div className="card h-100 p-3 interactive-card dash-kpi-tile" onClick={card.onClick} style={{cursor:'pointer', borderTop:`3px solid ${card.color}`, borderRadius:10}}>
                            <div className="d-flex align-items-center justify-content-between mb-2 dash-kpi-head">
                              <span className="dash-kpi-label" style={{fontSize:12, color:'#6B7280', fontWeight:500}} title={card.label as string}>{card.label}</span>
                              <div className={`${card.bgCls} p-2 rounded dash-kpi-icon`} style={(card as any).bgStyle}>{card.icon}</div>
                            </div>
                            <div className="dash-kpi-value" style={{fontSize:30, fontWeight:700, color:'#111827', lineHeight:1}}>{card.value ?? '—'}</div>
                            <div className="mt-2">
                              <div style={{height:4, borderRadius:4, background:'#F3F4F6', overflow:'hidden'}}>
                                <div style={{height:'100%', width:`${pct}%`, background:card.color, borderRadius:4, transition:'width 0.8s ease'}}/>
                              </div>
                              <span style={{fontSize:11, color:'#9CA3AF', marginTop:3, display:'block'}}>{pct}% of total</span>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}

              {/* status-donut */}
              {isVisible('status-donut') && modulePermissions.complianceTasks && (
                <div className="dash-grid-item" style={{flex:`0 1 calc(${(getW('status-donut',5)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('status-donut')}}>
                  <div className="card h-100 p-3 dash-chart-card" style={{minHeight:300, borderRadius:10}}>
                    <div style={{fontSize:14, fontWeight:600, color:'#374151', marginBottom:4}}>Task Status Distribution</div>
                    <div style={{fontSize:11, color:'#9CA3AF', marginBottom:8}}>Click a segment to view details</div>
                    {statusChartData.length > 0 ? (
                      <RcResponsiveContainer width="100%" height={230}>
                        <RcPieChart>
                          <RcPie data={statusChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value"
                            onClick={(entry: any) => {
                              if (entry.status === 'Done')               handleStatusBucketClick('done');
                              else if (entry.status === 'In Progress')   handleStatusBucketClick('in_progress');
                              else if (entry.status === 'Compliance')    handleStatusBucketClick('compliance');
                              else if (entry.status === 'Non Compliance')handleStatusBucketClick('non_compliance');
                            }}
                            style={{cursor:'pointer'}}>
                            {statusChartData.map((entry, i) => <RcCell key={i} fill={entry.color} stroke="none"/>)}
                          </RcPie>
                          <RcTooltip formatter={(v: any, n: any) => [`${v} tasks`, n]} contentStyle={{fontSize:12, borderRadius:8}}/>
                          <RcLegend iconType="circle" iconSize={9} wrapperStyle={{fontSize:12}}/>
                          <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:24, fontWeight:700, fill:'#111827'}}>{totalTasks}</text>
                          <text x="50%" y="57%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:11, fill:'#9CA3AF'}}>Total</text>
                        </RcPieChart>
                      </RcResponsiveContainer>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{height:230, color:'#9CA3AF', fontSize:13}}>No data available</div>
                    )}
                  </div>
                </div>
              )}

              {/* standards-bar */}
              {isVisible('standards-bar') && modulePermissions.standards && (
                <div className="dash-grid-item" style={{flex:`0 1 calc(${(getW('standards-bar',7)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('standards-bar')}}>
                  <div className="card h-100 p-3 dash-chart-card" style={{minHeight:300, borderRadius:10}}>
                    <div className="dash-card-header mb-1">
                      <div className="dash-card-heading" style={{fontSize:14, fontWeight:600, color:'#374151'}}>Compliance by Standard</div>
                      <span className="dash-card-hint" style={{fontSize:11, color:'#5a9b82', fontWeight:500}}>Click a bar to drill down</span>
                    </div>
                    <div style={{fontSize:11, color:'#9CA3AF', marginBottom:8}}>Hover for full name · click for control details</div>
                    {standardBarData.length > 0 ? (
                      <RcResponsiveContainer width="100%" height={230}>
                        <RcBarChart data={standardBarData} margin={{top:4, right:8, left:-10, bottom:30}}
                          onClick={(data: any) => {
                            const payload = data?.activePayload?.[0]?.payload;
                            if (!payload?.id) return;
                            openStandardDrill(payload.id, payload.fullName || payload.name);
                          }}
                          style={{cursor:'pointer'}}>
                          <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false}/>
                          <RcXAxis dataKey="name" tick={{fontSize:10, fill:'#6B7280'}} angle={-30} textAnchor="end" interval={0}/>
                          <RcYAxis tick={{fontSize:10, fill:'#6B7280'}}/>
                          <RcTooltip formatter={(v: any, n: any) => [v, n]} labelFormatter={(_: any, pl: any) => pl?.[0]?.payload?.fullName || ''} contentStyle={{fontSize:12, borderRadius:8, border:'1px solid #E5E7EB'}} cursor={{fill:'rgba(90,155,130,0.08)'}}/>
                          <RcLegend iconType="square" iconSize={10} wrapperStyle={{fontSize:11}}/>
                          <RcBar dataKey="Compliant"     stackId="a" fill="#22C55E"/>
                          <RcBar dataKey="Partial"       stackId="a" fill="#F59E0B"/>
                          <RcBar dataKey="Not Compliant" stackId="a" fill="#EF4444" radius={[3,3,0,0]}/>
                        </RcBarChart>
                      </RcResponsiveContainer>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{height:230, color:'#9CA3AF', fontSize:13}}>No standard data</div>
                    )}
                  </div>
                </div>
              )}

              {/* compliance-gauge */}
              {isVisible('compliance-gauge') && modulePermissions.complianceTasks && modulePermissions.standards && (
                <div className="dash-grid-item" style={{flex:`0 1 calc(${(getW('compliance-gauge',4)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(220px, 100%)', order:getOrder('compliance-gauge')}}>
                  <div className="card h-100 p-3 text-center dash-chart-card" style={{minHeight:210, borderRadius:10, cursor:'pointer', transition:'box-shadow 0.2s'}}
                    onClick={() => setComplianceGaugeDrillOpen(true)}
                    onMouseEnter={e => (e.currentTarget.style.boxShadow='0 4px 18px rgba(0,0,0,0.10)')}
                    onMouseLeave={e => (e.currentTarget.style.boxShadow='none')}>
                    <div style={{fontSize:14, fontWeight:600, color:'#374151', marginBottom:4}}>
                      Overall Compliance Rate
                      <span style={{fontSize:10, color:'#9CA3AF', fontWeight:400, marginInlineStart:6}}>click for breakdown ›</span>
                    </div>
                    <RcResponsiveContainer width="100%" height={155}>
                      <RcRadialBarChart cx="50%" cy="85%" innerRadius="65%" outerRadius="95%" startAngle={180} endAngle={0} data={gaugeData}>
                        <RcRadialBar dataKey="value" cornerRadius={6} background={{fill:'#F3F4F6'}}/>
                        <text x="50%" y="78%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:26, fontWeight:700, fill: gaugeData[0]?.fill || '#374151'}}>{complianceRate}%</text>
                        <text x="50%" y="93%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:11, fill:'#9CA3AF'}}>Compliance</text>
                      </RcRadialBarChart>
                    </RcResponsiveContainer>
                    <div className="d-flex justify-content-center flex-wrap gap-2 mt-1" style={{fontSize:10, color:'#6B7280'}}>
                      <span style={{color:'#22C55E'}}>● ≥70% Good</span>
                      <span style={{color:'#F59E0B'}}>● ≥40% Fair</span>
                      <span style={{color:'#EF4444'}}>● &lt;40% Poor</span>
                    </div>
                  </div>
                </div>
              )}

              {/* completion-gauge */}
              {isVisible('completion-gauge') && modulePermissions.complianceTasks && modulePermissions.standards && (
                <div className="dash-grid-item" style={{flex:`0 1 calc(${(getW('completion-gauge',4)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(220px, 100%)', order:getOrder('completion-gauge')}}>
                  {(() => {
                    const rate = totalTasks > 0 ? Math.round(((activeDashData?.done || 0) / totalTasks) * 100) : 0;
                    const gd = [{name:'Rate', value: rate, fill: rate >= 70 ? '#22C55E' : rate >= 40 ? '#F59E0B' : '#EF4444'}];
                    return (
                      <div className="card h-100 p-3 text-center dash-chart-card" style={{minHeight:210, borderRadius:10, cursor:'pointer', transition:'box-shadow 0.2s'}}
                        onClick={() => setCompletionGaugeDrillOpen(true)}
                        onMouseEnter={e => (e.currentTarget.style.boxShadow='0 4px 18px rgba(0,0,0,0.10)')}
                        onMouseLeave={e => (e.currentTarget.style.boxShadow='none')}>
                        <div style={{fontSize:14, fontWeight:600, color:'#374151', marginBottom:4}}>
                          Task Completion Rate
                          <span style={{fontSize:10, color:'#9CA3AF', fontWeight:400, marginInlineStart:6}}>click for breakdown ›</span>
                        </div>
                        <RcResponsiveContainer width="100%" height={155}>
                          <RcRadialBarChart cx="50%" cy="85%" innerRadius="65%" outerRadius="95%" startAngle={180} endAngle={0} data={gd}>
                            <RcRadialBar dataKey="value" cornerRadius={6} background={{fill:'#F3F4F6'}}/>
                            <text x="50%" y="78%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:26, fontWeight:700, fill: gd[0].fill}}>{rate}%</text>
                            <text x="50%" y="93%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:11, fill:'#9CA3AF'}}>Tasks Done</text>
                          </RcRadialBarChart>
                        </RcResponsiveContainer>
                        <div className="d-flex justify-content-center flex-wrap gap-2 mt-1" style={{fontSize:10, color:'#6B7280'}}>
                          <span style={{color:'#22C55E'}}>● ≥70% Good</span>
                          <span style={{color:'#F59E0B'}}>● ≥40% Fair</span>
                          <span style={{color:'#EF4444'}}>● &lt;40% Poor</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* quick-summary */}
              {isVisible('quick-summary') && (modulePermissions.standards || modulePermissions.complianceTasks || modulePermissions.survey) && (
                <div className="dash-grid-item" style={{flex:`0 1 calc(${(getW('quick-summary',4)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(220px, 100%)', order:getOrder('quick-summary')}}>
                  <div className="card h-100 p-3 dash-chart-card" style={{minHeight:210, borderRadius:10}}>
                    <div style={{fontSize:14, fontWeight:600, color:'#374151', marginBottom:16}}>Quick Summary</div>
                    {[
                      { label:'Standards Tracked', value: complianceForEachStandard?.length ?? 0, color:'#5a9b82', icon:'📋', onClick: () => navigate('/compliances'), permission: modulePermissions.standards },
                      { label:'Recent Actions',      value: (complianceActions as any[]).length,   color:'#3B82F6', icon:'⚡', onClick: () => { openTaskBucketDrill('in_progress'); }, permission: modulePermissions.complianceTasks },
                      { label:'Survey Assignments',  value: assignments.length,                    color:'#F59E0B', icon:'📝', onClick: () => navigate('/surveys'), permission: modulePermissions.survey },
                      { label:'Pending Tasks',       value:(activeDashData?.in_progress||0)+(activeDashData?.non_compliance||0), color:'#EF4444', icon:'⏳', permission: modulePermissions.complianceTasks, onClick: () => {
                          if (isRowFilterActive) {
                            const rows = filteredStdData
                              .filter((r: any) => r.status === 'In Progress' || r.compliance_status === 'Not Implemented')
                              .map((r: any) => ({
                                control: r.control || '', domain: r.domain || r.sub_domain || '',
                                status: r.status || '', compliance_status: r.compliance_status || '',
                                due_date: r.due_date ? new Date(r.due_date).toLocaleDateString() : '',
                              }));
                            const filterLabel = selectedStandardId
                              ? (complianceForEachStandard as any[]).find((s: any) => s.id === selectedStandardId)?.reference_name || t('Dashboard.selectedStandardFallback', { defaultValue: 'Selected Standard' })
                              : dueDateRangeLabel();
                            setStandardStatusDrill({ open: true, title: `${t('Dashboard.pendingTasksLabel', { defaultValue: 'Pending Tasks' })} — ${filterLabel}`, columns: STANDARD_STATUS_DRILL_COLUMNS, rows });
                          } else {
                            openTaskBucketDrill('in_progress');
                          }
                        } },
                    ].filter((s) => s.permission !== false).map((s, i) => (
                      <div key={i} className="d-flex align-items-center justify-content-between mb-3 dash-summary-row"
                        onClick={s.onClick}
                        style={{cursor:'pointer', padding:'6px 8px', borderRadius:8, transition:'background 0.15s'}}
                        onMouseEnter={e => (e.currentTarget.style.background='#F9FAFB')}
                        onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                        <div className="d-flex align-items-center gap-2 dash-summary-label">
                          <span style={{fontSize:16}}>{s.icon}</span>
                          <span style={{fontSize:12, color:'#6B7280'}}>{s.label}</span>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <span style={{fontSize:18, fontWeight:700, color:s.color}}>{s.value}</span>
                          <span style={{fontSize:11, color:'#D1D5DB'}}>›</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* remediation-kpis — plan counts by state, from GET /remediation */}
              {showRemediationWidgets && isVisible('remediation-kpis') && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('remediation-kpis',12)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('remediation-kpis')}}>
                  <div className="dash-card-header mb-2">
                    <div className="dash-card-heading" style={{fontSize:14, fontWeight:600, color:'#374151'}}>
                      🛠️ {t('Dashboard.remediationOverview', { defaultValue: 'Remediation Overview' })}
                      {priorityFilter && (
                        <span style={{marginInlineStart:8, fontSize:11, fontWeight:600, color:REMEDIATION_PRIORITY_COLORS[priorityFilter] || '#6B7280'}}>
                          · {t(`Remediation_Page.priority_${priorityFilter}`, { defaultValue: priorityFilter })}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/remediation')}
                      style={{background:'none', border:'none', padding:0, fontSize:11, fontWeight:600, color:'#5a9b82', cursor:'pointer'}}
                    >
                      {t('Dashboard.viewAllRemediations', { defaultValue: 'View all remediations' })} →
                    </button>
                  </div>
                  {remediationTotalCount > remediationRows.length && (
                    <div style={{marginBottom:8, fontSize:11, color:'#9CA3AF'}}>
                      {t('Dashboard.remediationTruncatedNote', {
                        defaultValue: 'Based on the most recent {{shown}} of {{total}} remediations.',
                        shown: remediationRows.length, total: remediationTotalCount,
                      })}
                    </div>
                  )}
                  <Row className="g-3">
                    {[
                      { bucket: 'all',         label: t('Dashboard.remediationTotal',      { defaultValue: 'Remediation Plans' }), value: remediationStats.total,      color:'#6366F1', icon: <FaTasks size={18} className="text-white"/> },
                      { bucket: 'not_started', label: t('Dashboard.remediationNotStarted', { defaultValue: 'Not Started' }),       value: remediationStats.notStarted,  color:'#9CA3AF', icon: <FaRegCircle size={18} className="text-white"/> },
                      { bucket: 'in_progress', label: t('Dashboard.remediationInProgress', { defaultValue: 'In Progress' }),       value: remediationStats.inProgress,  color:'#3B82F6', icon: <FaRegClock size={18} className="text-white"/> },
                      { bucket: 'done',        label: t('Dashboard.remediationDone',       { defaultValue: 'Completed' }),         value: remediationStats.done,        color:'#22C55E', icon: <FaRegCheckCircle size={18} className="text-white"/> },
                      { bucket: 'overdue',     label: t('Dashboard.remediationOverdue',    { defaultValue: 'Overdue' }),           value: remediationStats.overdue,     color:'#EF4444', icon: <FaExclamationTriangle size={18} className="text-white"/> },
                      { bucket: 'urgent',      label: t('Dashboard.remediationUrgent',     { defaultValue: 'High / Critical' }),   value: remediationStats.urgent,      color:'#F59E0B', icon: <FaExclamationCircle size={18} className="text-white"/> },
                    ].map((card, i) => {
                      const pct = remediationStats.total > 0 ? Math.round((card.value / remediationStats.total) * 100) : 0;
                      return (
                        <Col key={i} xs={12} sm={6} md={4} xl={2} className="dash-col">
                          <div className="card h-100 p-3 interactive-card dash-kpi-tile"
                            onClick={() => openRemediationDrill(card.bucket as RemediationBucket, card.label as string)}
                            style={{cursor:'pointer', borderTop:`3px solid ${card.color}`, borderRadius:10}}>
                            <div className="d-flex align-items-center justify-content-between mb-2 dash-kpi-head">
                              <span className="dash-kpi-label" style={{fontSize:12, color:'#6B7280', fontWeight:500}} title={card.label as string}>{card.label}</span>
                              <div className="p-2 rounded dash-kpi-icon" style={{backgroundColor:card.color}}>{card.icon}</div>
                            </div>
                            <div className="dash-kpi-value" style={{fontSize:28, fontWeight:700, color:'#111827', lineHeight:1}}>
                              {remediationLoading ? '…' : card.value}
                            </div>
                            <div className="mt-2">
                              <div style={{height:4, borderRadius:4, background:'#F3F4F6', overflow:'hidden'}}>
                                <div style={{height:'100%', width:`${pct}%`, background:card.color, borderRadius:4, transition:'width 0.8s ease'}}/>
                              </div>
                              <span style={{fontSize:11, color:'#9CA3AF', marginTop:3, display:'block'}}>
                                {t('Dashboard.remediationPctOfPlans', { defaultValue: '{{pct}}% of plans', pct })}
                              </span>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}

              {/* remediation-priority — donut; a slice toggles the priority filter */}
              {showRemediationWidgets && isVisible('remediation-priority') && (
                <div className="dash-grid-item" style={{flex:`0 1 calc(${(getW('remediation-priority',5)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('remediation-priority')}}>
                  <div className="card h-100 p-3 dash-chart-card" style={{minHeight:300, borderRadius:10}}>
                    <div style={{fontSize:14, fontWeight:600, color:'#374151', marginBottom:4}}>
                      {t('Dashboard.remediationByPriority', { defaultValue: 'Remediation by Priority' })}
                    </div>
                    <div style={{fontSize:11, color:'#9CA3AF', marginBottom:8}}>
                      {t('Dashboard.remediationPriorityHint', { defaultValue: 'Click a segment for its plans · click a legend label to filter the dashboard' })}
                    </div>
                    {remediationStats.byPriority.some(d => d.value > 0) ? (
                      <RcResponsiveContainer width="100%" height={230}>
                        <RcPieChart>
                          <RcPie
                            data={remediationStats.byPriority.filter(d => d.value > 0)}
                            cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value"
                            onClick={(entry: any) => {
                              const priority = entry?.name;
                              if (!priority) return;
                              openRemediationDrill(
                                `priority:${priority}` as RemediationBucket,
                                t(`Remediation_Page.priority_${priority}`, { defaultValue: priority }) as string,
                              );
                            }}
                            style={{cursor:'pointer'}}
                          >
                            {remediationStats.byPriority.filter(d => d.value > 0).map((entry, i) => (
                              <RcCell
                                key={i}
                                fill={entry.color}
                                stroke={priorityFilter === entry.name ? '#111827' : 'none'}
                                strokeWidth={priorityFilter === entry.name ? 2 : 0}
                              />
                            ))}
                          </RcPie>
                          <RcTooltip
                            formatter={(v: any, n: any) => [
                              t('Dashboard.remediationPlansCount', { defaultValue: '{{count}} plans', count: v }),
                              t(`Remediation_Page.priority_${n}`, { defaultValue: n }),
                            ]}
                            contentStyle={{fontSize:12, borderRadius:8}}
                          />
                          <RcLegend
                            iconType="circle" iconSize={9} wrapperStyle={{fontSize:12, cursor:'pointer'}}
                            formatter={(value: any) => t(`Remediation_Page.priority_${value}`, { defaultValue: value }) as string}
                            // Filtering the whole dashboard by priority moved here when the
                            // slice took over the drill-down; the filter card still has it too.
                            onClick={(entry: any) => applyPriorityFromChart(entry?.value)}
                          />
                          <text x="50%" y="44%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:24, fontWeight:700, fill:'#111827'}}>{remediationStats.total}</text>
                          <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" style={{fontSize:11, fill:'#9CA3AF'}}>
                            {t('Dashboard.remediationAvgProgress', { defaultValue: '{{pct}}% avg progress', pct: remediationStats.avgProgress })}
                          </text>
                        </RcPieChart>
                      </RcResponsiveContainer>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{height:230, color:'#9CA3AF', fontSize:13}}>
                        {remediationLoading
                          ? t('common.loading', { defaultValue: 'Loading…' })
                          : t('Dashboard.remediationNoData', { defaultValue: 'No remediation plans match the filter' })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* remediation-attention — overdue and highest-priority open plans */}
              {showRemediationWidgets && isVisible('remediation-attention') && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('remediation-attention',7)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('remediation-attention')}}>
                  <div className="card h-100 p-3 dash-table-card" style={{minHeight:300, borderRadius:10, borderInlineStart:'4px solid #EF4444'}}>
                    <div className="dash-card-header mb-1">
                      <div className="dash-card-heading">
                        <div style={{fontSize:14, fontWeight:600, color:'#374151'}}>
                          {t('Dashboard.remediationWatchlist', { defaultValue: 'Remediation Watchlist' })}
                        </div>
                        <div className="dash-card-subtitle">
                          {t('Dashboard.remediationWatchlistHint', { defaultValue: 'Open plans — overdue first, then by priority' })}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="dash-link-btn"
                        onClick={() => openRemediationDrill('watchlist', t('Dashboard.remediationWatchlist', { defaultValue: 'Remediation Watchlist' }) as string)}
                        style={{background:'none', border:'none', padding:0, fontSize:11, fontWeight:600, color:'#5a9b82', cursor:'pointer'}}
                      >
                        {t('Dashboard.viewAll', { defaultValue: 'View all' })} →
                      </button>
                    </div>
                    {remediationStats.watchlist.length > 0 ? (
                      <div className="dash-table-scroll">
                        <Table className="mb-0 dash-table dash-table--auto" style={{fontSize:12}}>
                          <thead>
                            <tr style={{color:'#6B7280'}}>
                              <th style={{fontWeight:600, border:'none'}}>{t('Remediation_Page.col_name')}</th>
                              <th style={{fontWeight:600, border:'none'}}>{t('Remediation_Page.col_priority')}</th>
                              <th style={{fontWeight:600, border:'none'}}>{t('Remediation_Page.col_due')}</th>
                              <th style={{fontWeight:600, border:'none'}}>{t('Remediation_Page.col_progress')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {remediationStats.watchlist.map((r: any) => {
                              const priority = String(r.priority || '').toLowerCase();
                              const overdue = remediationStats.isOverdue(r);
                              return (
                                <tr
                                  key={r.id}
                                  onClick={() => navigate(`/remediation/${r.id}`)}
                                  style={{cursor:'pointer'}}
                                  onMouseEnter={e => (e.currentTarget.style.background = '#F9FAFB')}
                                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <td style={{maxWidth:220, color:'#111827', fontWeight:500}}>
                                    <div className="dash-clamp-2" title={r.name}>{r.name}</div>
                                    <div style={{fontSize:11, color:'#9CA3AF'}}>{r.status || '—'}</div>
                                  </td>
                                  <td className="dash-col-nowrap">
                                    <span style={priorityBadgeStyle(priority)}>
                                      {t(`Remediation_Page.priority_${priority}`, { defaultValue: priority || '—' })}
                                    </span>
                                  </td>
                                  <td className="dash-col-nowrap" style={{color: overdue ? '#B3261E' : '#6B7280', fontWeight: overdue ? 600 : 400}}>
                                    {fmtRemediationDate(r.due_date || null)}
                                    {overdue && (
                                      <div style={{fontSize:10, fontWeight:700, textTransform:'uppercase'}}>
                                        {t('Remediation_Page.overdue')}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{minWidth:110}}>
                                    <div className="d-flex align-items-center gap-2">
                                      <div style={{flex:1, height:5, borderRadius:4, background:'#F3F4F6', overflow:'hidden'}}>
                                        <div style={{height:'100%', width:`${Number(r.progress) || 0}%`, background:'#5a9b82', borderRadius:4}}/>
                                      </div>
                                      <span style={{fontSize:11, color:'#6B7280'}}>{Number(r.progress) || 0}%</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{height:230, color:'#9CA3AF', fontSize:13}}>
                        {remediationLoading
                          ? t('common.loading', { defaultValue: 'Loading…' })
                          : t('Dashboard.remediationAllClear', { defaultValue: 'No open remediation plans — nothing needs attention' })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* workload — admin / manager / supervisor only */}
              {(isAdmin || roleConfig.showTeamView) && isVisible('workload') && modulePermissions.complianceTasks && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('workload',12)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('workload')}}>
                  <div className="card p-3 dash-chart-card" style={{borderRadius:10, borderInlineStart:'4px solid #5a9b82'}}>
                    <div className="dash-card-header mb-1">
                      <div className="dash-card-heading">
                        <span style={{fontSize:14, fontWeight:600, color:'#374151'}}>👥 Team Workload Distribution</span>
                        <span style={{fontSize:11, color:'#9CA3AF', fontWeight:400, marginInlineStart:8}}>Admin view — in-progress tasks per user</span>
                      </div>
                      <span className="dash-card-hint" style={{fontSize:11, color:'#5a9b82', fontWeight:500}}>Click a bar to view tasks</span>
                    </div>
                    {workloadBarData.length > 0 ? (
                      <RcResponsiveContainer width="100%" height={Math.max(200, workloadBarData.length * 36)}>
                        <RcBarChart data={workloadBarData} layout="vertical" margin={{top:4, right:50, left:70, bottom:4}}
                          onClick={(data: any) => {
                            if (data?.activePayload?.[0]?.payload) {
                              const p = data.activePayload[0].payload;
                              setWorkloadSearch('');
                              setWorkloadDrill({ open: true, user: p });
                            }
                          }}
                          style={{cursor:'pointer'}}>
                          <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false}/>
                          <RcXAxis type="number" tick={{fontSize:11, fill:'#6B7280'}} allowDecimals={false}/>
                          <RcYAxis type="category" dataKey="name" tick={{fontSize:11, fill:'#6B7280'}} width={65}/>
                          <RcTooltip
                            formatter={(v: any) => [`${v} task${v !== 1 ? 's' : ''}`, 'In Progress']}
                            labelFormatter={(_: any, pl: any) => pl?.[0]?.payload?.fullName || ''}
                            contentStyle={{fontSize:12, borderRadius:8, border:'1px solid #E5E7EB'}}
                            cursor={{fill:'rgba(59,130,246,0.08)'}}/>
                          <RcBar dataKey="tasks" fill="#3B82F6" radius={[0,6,6,0]} maxBarSize={26}
                            label={{position:'right', fontSize:11, fill:'#6B7280', formatter:(v:any)=>v}}/>
                        </RcBarChart>
                      </RcResponsiveContainer>
                    ) : (
                      <div className="text-center py-3" style={{color:'#9CA3AF', fontSize:13}}>No in-progress tasks assigned to users</div>
                    )}
                  </div>
                </div>
              )}

              {/* unassigned-work — supervisor / leader / super_admin; hidden on error or when all zero */}
              {isVisible('unassigned-work') && modulePermissions.complianceTasks && unassigned &&
                (unassigned.noSupervisor + unassigned.noManager + unassigned.neither) > 0 && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('unassigned-work',12)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('unassigned-work')}}>
                  <div className="card p-3 dash-chart-card" style={{borderRadius:10, borderInlineStart:'4px solid #F59E0B'}}>
                    <div style={{fontSize:14, fontWeight:600, color:'#374151', marginBottom:4}}>
                      {t('Dashboard.unassignedWork', { defaultValue: 'Unassigned Work' })}
                    </div>
                    <div style={{fontSize:11, color:'#9CA3AF', marginBottom:10}}>
                      {t('Dashboard.unassignedWorkHint', { defaultValue: 'Items with no supervisor or no manager assigned' })}
                    </div>
                    <div className="d-flex" style={{gap:12, flexWrap:'wrap', marginBottom:12}}>
                      {[
                        { key: 'noSupervisor', label: t('Dashboard.unassignedNoSupervisor', { defaultValue: 'No supervisor' }), value: unassigned.noSupervisor, color: '#F59E0B' },
                        { key: 'noManager', label: t('Dashboard.unassignedNoManager', { defaultValue: 'No manager' }), value: unassigned.noManager, color: '#8B5CF6' },
                        { key: 'neither', label: t('Dashboard.unassignedNeither', { defaultValue: 'Neither' }), value: unassigned.neither, color: '#EF4444' },
                      ].map((k) => (
                        <div key={k.key} style={{flex:'1 1 120px', background:'#F9FAFB', borderRadius:8, padding:'8px 12px', borderTop:`3px solid ${k.color}`}}>
                          <div style={{fontSize:22, fontWeight:700, color:'#111827'}}>{k.value}</div>
                          <div style={{fontSize:12, color:'#6B7280'}}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    {unassigned.byStandard.length > 0 && (
                      <RcResponsiveContainer width="100%" height={Math.max(160, unassigned.byStandard.length * 34)}>
                        <RcBarChart data={unassigned.byStandard} layout="vertical" margin={{top:4, right:30, left:10, bottom:4}}>
                          <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false}/>
                          <RcXAxis type="number" tick={{fontSize:11, fill:'#6B7280'}} allowDecimals={false}/>
                          <RcYAxis type="category" dataKey="referenceName" tick={{fontSize:11, fill:'#6B7280'}} width={120}/>
                          <RcTooltip contentStyle={{fontSize:12, borderRadius:8, border:'1px solid #E5E7EB'}} cursor={{fill:'rgba(245,158,11,0.08)'}}/>
                          <RcLegend iconType="circle" iconSize={9} wrapperStyle={{fontSize:12}}/>
                          <RcBar dataKey="noSupervisor" name={t('Dashboard.unassignedNoSupervisor', { defaultValue: 'No supervisor' }) as string} fill="#F59E0B" radius={[0,4,4,0]} maxBarSize={14}/>
                          <RcBar dataKey="noManager" name={t('Dashboard.unassignedNoManager', { defaultValue: 'No manager' }) as string} fill="#8B5CF6" radius={[0,4,4,0]} maxBarSize={14}/>
                        </RcBarChart>
                      </RcResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* my-tasks — non-admin / non-manager */}
              {!roleConfig.showTeamView && isVisible('my-tasks') && modulePermissions.complianceTasks && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('my-tasks',12)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('my-tasks')}}>
                  <div className="card p-0 dash-table-card" style={{borderRadius:10, borderInlineStart:'4px solid #3B82F6', overflow:'hidden'}}>
                    <div className="dash-card-header p-3" style={{borderBottom:'1px solid #F3F4F6'}}>
                      <div className="d-flex align-items-center flex-wrap gap-2 dash-card-heading">
                        <span style={{fontSize:16}}>📋</span>
                        <span style={{fontSize:14, fontWeight:600, color:'#374151'}}>My Tasks</span>
                        <span style={{padding:'2px 8px', borderRadius:12, background:'#DBEAFE', color:'#1D4ED8', fontSize:11, fontWeight:600}}>
                          {(inProgressData as any[]).length} in progress
                        </span>
                        {(completedTasksData as any[]).length > 0 && (
                          <span style={{padding:'2px 8px', borderRadius:12, background:'#DCFCE7', color:'#16A34A', fontSize:11, fontWeight:600}}>
                            {(completedTasksData as any[]).length} done
                          </span>
                        )}
                      </div>
                      <button type="button" onClick={() => { openTaskBucketDrill('in_progress'); }}
                        className="dash-view-all-btn">
                        View All
                      </button>
                    </div>
                    {(inProgressData as any[]).length === 0 ? (
                      <div className="text-center py-4" style={{color:'#9CA3AF', fontSize:13}}>No in-progress tasks assigned to you</div>
                    ) : (
                      <div className="dash-table-scroll dash-table-scroll--tall">
                        <table className="table table-sm table-hover mb-0 dash-table dash-table--auto dash-table--flat">
                          <thead>
                            <tr>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Control / Task</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Domain</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Compliance Status</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Task Status</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(inProgressData as any[]).slice(0, 8).map((item: any, idx: number) => {
                              const cs = item.compliance_status || '';
                              const csBg = cs === 'Implemented' ? '#DCFCE7' : cs === 'Partially Implemented' ? '#FEF3C7' : cs === 'Not Implemented' ? '#FEE2E2' : '#F3F4F6';
                              const csColor = cs === 'Implemented' ? '#16A34A' : cs === 'Partially Implemented' ? '#D97706' : cs === 'Not Implemented' ? '#DC2626' : '#6B7280';
                              const canNav = item.control?.id && item.compliance_id && item.domain?.id;
                              return (
                                <tr key={idx}
                                  style={{cursor: canNav ? 'pointer' : 'default', transition:'background 0.12s'}}
                                  onClick={() => { if (canNav) navigate(`/compliances/view-control/${item.control.id}/${item.compliance_id}/${item.domain.id}`); }}>
                                  <td style={{padding:'10px 16px', fontSize:13, fontWeight:500, color:'#111827', verticalAlign:'middle', minWidth:180, maxWidth:260}}>
                                    <div className="dash-clamp-2" title={item.control?.name || ''}>
                                      {item.control?.keyControl && <span style={{marginInlineEnd:6, fontSize:10, background:'#FEF3C7', color:'#D97706', padding:'1px 6px', borderRadius:8, fontWeight:600}}>Key</span>}
                                      {item.control?.name || '—'}
                                    </div>
                                  </td>
                                  <td style={{padding:'10px 16px', fontSize:12, color:'#6B7280', verticalAlign:'middle', minWidth:120, maxWidth:220}}>
                                    <div className="dash-clamp-2" title={item.domain?.en_name || ''}>{item.domain?.en_name || '—'}</div>
                                  </td>
                                  <td className="dash-col-nowrap" style={{padding:'10px 16px', verticalAlign:'middle'}}>
                                    {cs ? <span className="dash-status-badge" style={{background:csBg, color:csColor}}>{cs}</span> : '—'}
                                  </td>
                                  <td className="dash-col-nowrap" style={{padding:'10px 16px', verticalAlign:'middle'}}>
                                    <span className="dash-status-badge" style={{background:'#DBEAFE', color:'#1D4ED8'}}>In Progress</span>
                                  </td>
                                  <td className="dash-row-chevron" style={{padding:'10px 16px', verticalAlign:'middle', textAlign:'end'}}>
                                    {canNav && <span style={{fontSize:16, color:'#D1D5DB'}}>›</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {(inProgressData as any[]).length > 8 && (
                          <div className="text-center py-2" style={{borderTop:'1px solid #F3F4F6'}}>
                            <button onClick={() => { openTaskBucketDrill('in_progress'); }}
                              style={{fontSize:12, color:'#3B82F6', fontWeight:500, background:'none', border:'none', cursor:'pointer'}}>
                              + {(inProgressData as any[]).length - 8} more tasks — View All
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* my-function-surveys — surveys linked to any function the viewer belongs to */}
              {isVisible('my-function-surveys') && modulePermissions.survey && (
                <div className="dash-grid-item dash-grid-item--wide" style={{flex:`0 1 calc(${(getW('my-function-surveys',12)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(280px, 100%)', order:getOrder('my-function-surveys')}}>
                  <div className="card p-0 dash-table-card" style={{borderRadius:10, borderInlineStart:'4px solid #8B5CF6', overflow:'hidden'}}>
                    <div className="dash-card-header p-3" style={{borderBottom:'1px solid #F3F4F6'}}>
                      <div className="d-flex align-items-center flex-wrap gap-2 dash-card-heading">
                        <span style={{fontSize:16}}>📝</span>
                        <span style={{fontSize:14, fontWeight:600, color:'#374151'}}>My Surveys</span>
                        <span style={{padding:'2px 8px', borderRadius:12, background:'#F5F3FF', color:'#8B5CF6', fontSize:11, fontWeight:600}}>
                          {myFunctionSurveys.length}
                        </span>
                      </div>
                      <button type="button" onClick={() => navigate('/surveys')}
                        className="dash-view-all-btn" style={{color:'#8B5CF6', borderColor:'#EDE9FE'}}>
                        View All
                      </button>
                    </div>
                    {myFunctionSurveysLoading ? (
                      <div className="text-center py-4" style={{color:'#9CA3AF', fontSize:13}}>Loading…</div>
                    ) : myFunctionSurveys.length === 0 ? (
                      <div className="text-center py-4" style={{color:'#9CA3AF', fontSize:13}}>No surveys linked to your function(s)</div>
                    ) : (
                      <div className="dash-table-scroll dash-table-scroll--tall">
                        <table className="table table-sm table-hover mb-0 dash-table dash-table--auto dash-table--flat">
                          <thead>
                            <tr>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Survey</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Function(s)</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Status</th>
                              <th style={{fontSize:11, fontWeight:600, color:'#6B7280', padding:'8px 16px', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap'}}>Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myFunctionSurveys.map((survey: any) => {
                              const functionNames = (survey.publicSurveyFunctions || [])
                                .map((pf: any) => pf.Function?.name)
                                .filter(Boolean)
                                .join(', ');
                              return (
                                <tr key={survey.id} style={{cursor:'pointer', transition:'background 0.12s'}}
                                  onClick={() => navigate(`/assessment-survey/${survey.id}`)}>
                                  <td style={{padding:'10px 16px', fontSize:13, fontWeight:500, color:'#111827', verticalAlign:'middle', minWidth:180, maxWidth:280}}>
                                    <div className="dash-clamp-2" title={survey.name_en || survey.name_ar || ''}>{survey.name_en || survey.name_ar || '—'}</div>
                                  </td>
                                  <td style={{padding:'10px 16px', fontSize:12, color:'#6B7280', verticalAlign:'middle', minWidth:140, maxWidth:240}}>
                                    <div className="dash-clamp-2" title={functionNames}>{functionNames || '—'}</div>
                                  </td>
                                  <td className="dash-col-nowrap" style={{padding:'10px 16px', verticalAlign:'middle'}}>
                                    <span className="dash-status-badge" style={{background:'#F5F3FF', color:'#8B5CF6'}}>
                                      {survey.approval_status || '—'}
                                    </span>
                                  </td>
                                  <td style={{padding:'10px 16px', fontSize:12, color:'#6B7280', verticalAlign:'middle', whiteSpace:'nowrap'}}>
                                    {survey.createdAt ? new Date(survey.createdAt).toLocaleDateString() : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Custom user-created charts */}
              {customCharts.filter(cc => isVisible(cc.id)).map(cc => (
                <div key={cc.id} className="dash-grid-item" style={{flex:`0 1 calc(${(getW(cc.id,cc.width)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(220px, 100%)', order:getOrder(cc.id)}}>
                  {renderCustomChart(cc)}
                </div>
              ))}

              {/* Dynamic charts — saved to backend, visible per group/function */}
              {dynEntries.filter(({ config: dc }) => isVisible(dc.id)).map(({ config: dc, isOwner, isShared: shared }) => (
                <div key={dc.id} className="dash-grid-item" style={{flex:`0 1 calc(${(getW(dc.id, dc.width)/12*100).toFixed(2)}% - 12px)`, minWidth:'min(260px, 100%)', order:getOrder(dc.id), position:'relative'}}>
                  {shared && (
                    <div style={{position:'absolute', top:8, right: isOwner ? 36 : 10, zIndex:2, fontSize:9, padding:'2px 7px', borderRadius:10, background:'#EFF6FF', color:'#3B82F6', fontWeight:700, pointerEvents:'none'}}>
                      shared
                    </div>
                  )}
                  <DynamicChart
                    config={dc}
                    onRemove={isOwner ? () => handleRemoveDynamic(dc.id) : undefined}
                  />
                </div>
              ))}

            </div>{/* end flex grid */}

            <DrillDownModal
              isOpen={customDrill.open}
              onClose={() => setCustomDrill(p => ({ ...p, open: false }))}
              title={customDrill.title}
              subtitle={customDrill.subtitle}
              loading={customDrill.loading}
              columns={customDrill.columns}
              rows={customDrill.rows}
            />

            <DrillDownModal
              isOpen={standardStatusDrill.open}
              onClose={() => setStandardStatusDrill(p => ({ ...p, open: false }))}
              title={standardStatusDrill.title}
              columns={standardStatusDrill.columns}
              rows={standardStatusDrill.rows}
            />

            <DrillDownModal
              isOpen={standardDrill.open}
              onClose={() => setStandardDrill(p => ({ ...p, open: false }))}
              title={`Controls — ${standardDrill.name}`}
              subtitle="Every compliance task linked to this standard"
              columns={STANDARD_STATUS_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchStandardDrillPage}
              onRowClick={(row) => {
                if (!row.control_id || !row.compliance_id || !row.domain_id) return;
                setStandardDrill(p => ({ ...p, open: false }));
                navigate(`/compliances/view-control/${row.control_id}/${row.compliance_id}/${row.domain_id}`);
              }}
            />

            <DrillDownModal
              isOpen={complianceGaugeDrillOpen}
              onClose={() => setComplianceGaugeDrillOpen(false)}
              title="Compliance Rate — Standard Breakdown"
              subtitle={`Overall: ${complianceRate}% · click a row to drill into its controls`}
              columns={GAUGE_DRILL_COLUMNS}
              rows={gaugeDrillRows}
              onRowClick={(row) => { setComplianceGaugeDrillOpen(false); openStandardDrill(row.id, row.fullName); }}
            />

            <DrillDownModal
              isOpen={completionGaugeDrillOpen}
              onClose={() => setCompletionGaugeDrillOpen(false)}
              title="Task Completion — Standard Breakdown"
              subtitle="Click a row to drill into its controls"
              columns={GAUGE_DRILL_COLUMNS}
              rows={gaugeDrillRows}
              onRowClick={(row) => { setCompletionGaugeDrillOpen(false); openStandardDrill(row.id, row.fullName); }}
            />

            <DrillDownModal
              isOpen={surveyResponsesDrillOpen}
              onClose={() => setSurveyResponsesDrillOpen(false)}
              title="Survey Responses"
              subtitle="Every recorded answer, by user and survey"
              columns={SURVEY_RESPONSES_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchSurveyResponsesPage}
            />

            <DrillDownModal
              isOpen={participantsDrillOpen}
              onClose={() => setParticipantsDrillOpen(false)}
              title="Survey Participants"
              subtitle="Every user assigned to a survey"
              columns={PARTICIPANTS_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchParticipantsPage}
            />

            <DrillDownModal
              isOpen={surveysDrillOpen}
              onClose={() => setSurveysDrillOpen(false)}
              title="Surveys"
              subtitle="Every survey in the tenant"
              columns={SURVEYS_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchSurveysDrillPage}
            />

            <DrillDownModal
              isOpen={workloadDrill.open}
              onClose={() => setWorkloadDrill({ open: false, user: null })}
              title={`In-Progress Tasks — ${workloadDrill.user?.fullName || workloadDrill.user?.name || ''}`}
              subtitle="Open compliance tasks assigned to this function"
              columns={WORKLOAD_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchWorkloadPage}
            />

            <DrillDownModal
              isOpen={taskBucketDrill.open}
              onClose={() => setTaskBucketDrill(p => ({ ...p, open: false }))}
              title={taskBucketDrill.title}
              subtitle={taskBucketDrill.subtitle}
              columns={STANDARD_STATUS_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchTaskBucketPage}
              onRowClick={(row) => {
                if (!row.control_id || !row.compliance_id || !row.domain_id) return;
                setTaskBucketDrill(p => ({ ...p, open: false }));
                navigate(`/compliances/view-control/${row.control_id}/${row.compliance_id}/${row.domain_id}`);
              }}
            />

            <DrillDownModal
              isOpen={remediationDrill.open}
              onClose={() => setRemediationDrill(p => ({ ...p, open: false }))}
              title={remediationDrill.title}
              subtitle={remediationDrill.subtitle}
              columns={REMEDIATION_DRILL_COLUMNS}
              rows={[]}
              fetchPage={fetchRemediationDrillPage}
              onRowClick={(row) => { setRemediationDrill(p => ({ ...p, open: false })); navigate(`/remediation/${row.id}`); }}
            />
          </>)}

          {chartTab === 'objectives' && (<>
          {/* ===== Compliance Objectives Overview ===== */}
          <Row className="mb-2 pt-2">
            <Col xs={12}>
              <h5 className="dash-section-title">{t('Dashboard.objectivesOverview')}</h5>
            </Col>
          </Row>

          {objectiveStats.total === 0 ? (
            <Row className="mb-3">
              <Col xs={12}>
                <div className="card p-4 text-center">
                  <h6 className="mb-2">{t('Dashboard.noObjectivesFound')}</h6>
                  <p className="text-muted mb-3">{t('Dashboard.generateObjectivesHint')}</p>
                  <div>
                    <Button color="primary" size="sm" onClick={() => navigate('/objective')}>
                      {t('Dashboard.goToObjectives')}
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <Row className="mb-3">
                <Col xs={12} sm={6} xl={3} className="rounded dash-col">
                  <div className="card h-100 p-3">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h6 className="fw-semi-bold">{t('Dashboard.totalObjectives')}</h6>
                        <h2 className="mt-2">{objectiveStats.total}</h2>
                      </div>
                      <div className="bg-success p-2 rounded">
                        <FaTasks size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6} xl={3} className="rounded dash-col">
                  <div className="card h-100 p-3">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h6 className="fw-semi-bold">{t('Dashboard.highPriority')}</h6>
                        <h2 className="mt-2">{objectiveStats.highPriority}</h2>
                      </div>
                      <div className="bg-danger p-2 rounded">
                        <FaExclamationCircle size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6} xl={3} className="rounded dash-col">
                  <div className="card h-100 p-3">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h6 className="fw-semi-bold">{t('Dashboard.highEffort')}</h6>
                        <h2 className="mt-2">{objectiveStats.highEffort}</h2>
                      </div>
                      <div className="bg-warning p-2 rounded">
                        <FaExclamationTriangle size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6} xl={3} className="rounded dash-col">
                  <div className="card h-100 p-3">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h6 className="fw-semi-bold">{t('Dashboard.regulatoryFrameworks')}</h6>
                        <h2 className="mt-2">{objectiveStats.frameworks}</h2>
                      </div>
                      <div className="bg-primary p-2 rounded">
                        <HiOutlineDocumentText size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={12} xl={6} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.objectivesByPriority')}</h6>
                    <RcResponsiveContainer width="100%" height={300}>
                      <RcPieChart>
                        <RcPie
                          data={objectiveStats.priorityData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={2}
                          label={({ percent }: any) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {objectiveStats.priorityData.map((entry, i) => (
                            <RcCell
                              key={entry.name}
                              fill={OBJECTIVE_PRIORITY_COLORS[i % OBJECTIVE_PRIORITY_COLORS.length]}
                            />
                          ))}
                        </RcPie>
                        <RcTooltip />
                        <RcLegend verticalAlign="bottom" iconType="circle" />
                      </RcPieChart>
                    </RcResponsiveContainer>
                  </div>
                </Col>
                <Col xs={12} xl={6} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.objectivesByEffort')}</h6>
                    <RcResponsiveContainer width="100%" height={300}>
                      <RcBarChart data={objectiveStats.effortData}>
                        <RcCartesianGrid strokeDasharray="3 3" vertical={false} />
                        <RcXAxis dataKey="name" />
                        <RcYAxis allowDecimals={false} />
                        <RcTooltip />
                        <RcBar dataKey="value" radius={[6, 6, 0, 0]}>
                          {objectiveStats.effortData.map((entry, i) => (
                            <RcCell
                              key={entry.name}
                              fill={OBJECTIVE_EFFORT_COLORS[i % OBJECTIVE_EFFORT_COLORS.length]}
                            />
                          ))}
                        </RcBar>
                      </RcBarChart>
                    </RcResponsiveContainer>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={12} xl={6} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.objectivesTimeline')}</h6>
                    {objectiveTimeline.length === 0 ? (
                      <div className="text-muted text-center py-5">{t('Dashboard.noDataAvailable')}</div>
                    ) : (
                      <RcResponsiveContainer width="100%" height={300}>
                        <RcBarChart data={objectiveTimeline}>
                          <RcCartesianGrid strokeDasharray="3 3" vertical={false} />
                          <RcXAxis dataKey="name" />
                          <RcYAxis allowDecimals={false} />
                          <RcTooltip />
                          <RcLegend verticalAlign="bottom" iconType="circle" />
                          <RcBar dataKey="value" name={t('Dashboard.objectivesDue') as string} fill="#14B8A6" radius={[6, 6, 0, 0]} />
                        </RcBarChart>
                      </RcResponsiveContainer>
                    )}
                  </div>
                </Col>
                <Col xs={12} xl={6} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.objectivesByRegulation')}</h6>
                    {objectiveRegulation.length === 0 ? (
                      <div className="text-muted text-center py-5">{t('Dashboard.noDataAvailable')}</div>
                    ) : (
                      <RcResponsiveContainer width="100%" height={300}>
                        <RcBarChart data={objectiveRegulation} layout="vertical" margin={{ left: 24, right: 16 }}>
                          <RcCartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <RcXAxis type="number" allowDecimals={false} />
                          <RcYAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} tickFormatter={(v: string) => truncateLabel(v, 24)} />
                          <RcTooltip />
                          <RcBar dataKey="value" radius={[0, 6, 6, 0]}>
                            {objectiveRegulation.map((entry, i) => (
                              <RcCell key={entry.name} fill={REGULATION_BAR_COLORS[i % REGULATION_BAR_COLORS.length]} />
                            ))}
                          </RcBar>
                        </RcBarChart>
                      </RcResponsiveContainer>
                    )}
                  </div>
                </Col>
              </Row>
            </>
          )}
          </>)}

          {chartTab === 'controls' && (<>
          {/* ===== Control Mapper Overview ===== */}
          <Row className="mb-2 pt-2">
            <Col xs={12}>
              <h5 className="dash-section-title">{t('Dashboard.controlMapperOverview')}</h5>
            </Col>
          </Row>

          {controlMapperStats.total === 0 ? (
            <Row className="mb-3">
              <Col xs={12}>
                <div className="card p-4 text-center">
                  <h6 className="mb-0 text-muted">{t('Dashboard.noControlMapperData')}</h6>
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <Row className="mb-3">
                <Col xs={12} xl={5} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.individualVsGrouped')}</h6>
                    <RcResponsiveContainer width="100%" height={300}>
                      <RcPieChart>
                        <RcPie
                          data={controlMapperStats.distribution}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={2}
                          label={({ percent }: any) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {controlMapperStats.distribution.map((entry, i) => (
                            <RcCell key={entry.name} fill={CONTROL_DIST_COLORS[i % CONTROL_DIST_COLORS.length]} />
                          ))}
                        </RcPie>
                        <RcTooltip />
                        <RcLegend verticalAlign="bottom" iconType="circle" />
                      </RcPieChart>
                    </RcResponsiveContainer>
                  </div>
                </Col>
                <Col xs={12} xl={7} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.topOwnersByControls')}</h6>
                    {controlMapperStats.topOwners.length === 0 ? (
                      <div className="text-muted text-center py-5">{t('Dashboard.noOwnerData')}</div>
                    ) : (
                      <RcResponsiveContainer width="100%" height={300}>
                        <RcBarChart data={controlMapperStats.topOwners}>
                          <RcCartesianGrid strokeDasharray="3 3" vertical={false} />
                          <RcXAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} tickFormatter={(v: string) => truncateLabel(v, 16)} />
                          <RcYAxis allowDecimals={false} />
                          <RcTooltip />
                          <RcBar dataKey="value" name={t('Dashboard.controls') as string} fill={OWNER_BAR_COLOR} radius={[6, 6, 0, 0]} />
                        </RcBarChart>
                      </RcResponsiveContainer>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={12}>
                  <div className="card p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.mappedControlsDetail')}</h6>
                    <div className="table-responsive dash-table-scroll dash-table-scroll--tall">
                      <Table bordered hover className="mb-0 dash-table dash-table--auto">
                        <thead>
                          <tr>
                            <th>{t('Dashboard.code')}</th>
                            <th>{t('Dashboard.controlName')}</th>
                            <th>{t('Dashboard.description')}</th>
                            <th>{t('Dashboard.objective')}</th>
                            <th>{t('Dashboard.ownerDepartment')}</th>
                            <th>{t('Dashboard.mandatoryType')}</th>
                            <th>{t('Dashboard.source')}</th>
                            <th>{t('Dashboard.groupName')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {controlMapperStats.rows.map((row, i) => (
                            <tr key={`${row.code}-${i}`}>
                              <td className="dash-col-nowrap">{row.code}</td>
                              <td style={{ minWidth: 160 }}>{row.name}</td>
                              <td style={{ minWidth: 220 }}><div className="dash-clamp-2" title={row.description || ''}>{row.description}</div></td>
                              <td>{row.objective}</td>
                              <td>{row.owner}</td>
                              <td>{row.mandatoryType}</td>
                              <td>{row.source}</td>
                              <td>{row.groupName}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}

          </>)}

          {chartTab === 'assessment' && (<>
          {/* ===== Assessment Overview ===== */}
          {assessmentStats && (
            <>
              <Row className="mb-3">
                <Col xs={12} xl={4} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.assessmentOverallScore')}</h6>
                    <div style={{ position: 'relative', width: 220, height: 220, margin: '0 auto' }}>
                      <RcResponsiveContainer width="100%" height="100%">
                        <RcPieChart>
                          <RcPie
                            data={[
                              { name: 'score', value: assessmentStats.overall },
                              { name: 'rest', value: 100 - assessmentStats.overall },
                            ]}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={105}
                            startAngle={90}
                            endAngle={-270}
                            stroke="none"
                          >
                            <RcCell fill={ASSESSMENT_SCORE_COLOR} />
                            <RcCell fill="#E5E7EB" />
                          </RcPie>
                        </RcPieChart>
                      </RcResponsiveContainer>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '2.25rem', fontWeight: 700 }}>{assessmentStats.overall}%</span>
                        <span className="text-muted">{t('Dashboard.overallScore')}</span>
                      </div>
                    </div>
                    <div className="text-center mt-3">
                      <div><strong>{t('Dashboard.totalScore')}</strong> {assessmentStats.actualScore} / {assessmentStats.maxScore}</div>
                      <div className="text-success"><strong>{t('Dashboard.questionsAnswered')}</strong> {assessmentStats.answered} / {assessmentStats.total}</div>
                      <div className="text-success">{clampPct(assessmentStats.completionPct)}% {t('Dashboard.completed')}</div>
                    </div>
                  </div>
                </Col>
                <Col xs={12} xl={8} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.assessmentScoreByCategory')}</h6>
                    {assessmentStats.categoryScores.length === 0 ? (
                      <div className="text-muted text-center py-5">{t('Dashboard.noCategoryScoreData')}</div>
                    ) : (
                      <RcResponsiveContainer width="100%" height={320}>
                        <RcBarChart data={assessmentStats.categoryScores}>
                          <RcCartesianGrid strokeDasharray="3 3" vertical={false} />
                          <RcXAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                          <RcYAxis domain={[0, 100]} />
                          <RcTooltip formatter={(v: any) => `${v}%`} />
                          <RcBar dataKey="value" name={t('Dashboard.scorePercent') as string} radius={[6, 6, 0, 0]}>
                            {assessmentStats.categoryScores.map((entry: any) => (
                              <RcCell key={entry.fullName} fill={entry.color} />
                            ))}
                          </RcBar>
                        </RcBarChart>
                      </RcResponsiveContainer>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={12} xl={5} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.completionStatus')}</h6>
                    <RcResponsiveContainer width="100%" height={300}>
                      <RcPieChart>
                        <RcPie
                          data={assessmentStats.completionDonut}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={2}
                          label={({ percent }: any) => `${(percent * 100).toFixed(1)}%`}
                        >
                          {assessmentStats.completionDonut.map((entry: any) => (
                            <RcCell key={entry.name} fill={entry.color} />
                          ))}
                        </RcPie>
                        <RcTooltip />
                        <RcLegend verticalAlign="bottom" iconType="circle" />
                      </RcPieChart>
                    </RcResponsiveContainer>
                  </div>
                </Col>
                <Col xs={12} xl={7} className="mb-3 dash-col">
                  <div className="card h-100 p-3">
                    <h6 className="fw-semi-bold mb-3">{t('Dashboard.categoryPerformanceComparison')}</h6>
                    {assessmentStats.radar.length < 3 ? (
                      <div className="text-muted text-center py-5">{t('Dashboard.noCategoryData')}</div>
                    ) : (
                      <RcResponsiveContainer width="100%" height={340}>
                        <RcRadarChart data={assessmentStats.radar} cx="50%" cy="50%" outerRadius="75%">
                          <RcPolarGrid />
                          <RcPolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
                          <RcPolarRadiusAxis angle={90} domain={[0, 100]} />
                          <RcRadar name={t('Dashboard.scorePercent') as string} dataKey="score" stroke={ASSESSMENT_SCORE_COLOR} fill={ASSESSMENT_SCORE_COLOR} fillOpacity={0.3} />
                          <RcRadar name={t('Dashboard.completionPercent') as string} dataKey="completion" stroke={ASSESSMENT_COMPLETION_COLOR} fill={ASSESSMENT_COMPLETION_COLOR} fillOpacity={0.25} />
                          <RcLegend verticalAlign="bottom" iconType="circle" />
                          <RcTooltip formatter={(v: any) => `${v}%`} />
                        </RcRadarChart>
                      </RcResponsiveContainer>
                    )}
                  </div>
                </Col>
              </Row>
            </>
          )}
          </>)}

  {chartTab === 'surveys' && (<>
  {/* Completion Rate Card */}



<div className="row g-4 mb-3 pt-3">
  <div className="col-sm-6 col-xl-3 dash-col">
    <div className="card  h-100 p-3">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className=" mb-2">Completion Tasks Rate</h6>
          <h3 className="mb-1">{completionRate ? `${completionRate.toFixed(2)}%` : 'N/A'}</h3>
        </div>
        <div className="bg-gray text-white rounded p-2">
          <GrLineChart size={24} />
        </div>
      </div>
    </div>
  </div>

  {/* Total Responses Card */}
  <div className="col-sm-6 col-xl-3 dash-col">
    <div className="card h-100 p-3 interactive-card" onClick={handleSurveyResponsesClick} title="Click to view details">
      <div style={{ position: 'relative' }}>
        {/* Top-right arrow icon */}
        <div
          style={{ position: 'absolute', top: -14, right: -6, zIndex: 2 }}
          onClick={(e) => { e.stopPropagation(); handleSurveyResponsesClick(); }}
          title={t('Dashboard.View_Details') as string}
        >
          <FaExternalLinkAlt size={20} style={{ color: '#D1D5DB' }} />
        </div>
        
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-2">Total Response Surveys</h6>
            <h2 className="mb-1">{totalResponses.toLocaleString()}</h2>
          </div>
          <div className="bg-info text-white rounded p-2">
            <HiOutlineChartBar size={24}  />
          </div>
        </div>
      </div>
    </div>
  </div>

     {/* Unique Participants Card */}
   <div className="col-sm-6 col-xl-3 dash-col">
     <div className="card h-100 p-3 interactive-card" onClick={handleUserParticipateClick} title="Click to view details">
       <div style={{ position: 'relative' }}>
         {/* Top-right arrow icon */}
         <div
           style={{ position: 'absolute', top: -14, right: -6, zIndex: 2 }}
           onClick={(e) => { e.stopPropagation(); handleUserParticipateClick(); }}
           title={t('Dashboard.View_Details') as string}
         >
           <FaExternalLinkAlt size={20} style={{ color: '#D1D5DB' }} />
         </div>
         
         <div className="d-flex justify-content-between align-items-center">
           <div>
             <h6 className="mb-2">User Participate Surveys</h6>
             <h2 className="mb-1">{participantCount.toLocaleString()}</h2>
           
           </div>
           <div className=" text-white rounded p-2 " style={{"backgroundColor":" #6f42c1"}}>
             <PiChartDonutThin size={24}  />
           </div>
         </div>
       </div>
     </div>
   </div>

     {/* Total Surveys Card */}
   <div className="col-sm-6 col-xl-3 dash-col">
     <div className="card h-100 p-3 interactive-card" onClick={handleTotalSurveysClick} title="Click to view details">
       <div style={{ position: 'relative' }}>
         {/* Top-right arrow icon */}
         <div
           style={{ position: 'absolute', top: -14, right: -6, zIndex: 2 }}
           onClick={(e) => { e.stopPropagation(); handleTotalSurveysClick(); }}
           title={t('Dashboard.View_Details') as string}
         >
           <FaExternalLinkAlt size={20} style={{ color: '#D1D5DB' }} />
         </div>
         
         <div className="d-flex justify-content-between align-items-center">
           <div>
             <h6 className="mb-2">Total Surveys</h6>
             <h2 className="mb-1">{surveyCount.toLocaleString()}</h2>
           </div>
           <div className=" text-white rounded p-2 bg-success">
             <HiOutlineChartPie size={24}  />
           </div>
         </div>
       </div>
     </div>
   </div>
</div>
  </>)}

</div>
       

          {chartTab === 'surveys' && (
          <Row>
          <Col xs={12} xl={4} className="dash-col">
  <Widget className="custom-widget">
    <h5 className="widget-title" >{t("Dashboard.Controls Per Compliance")}</h5>
    <hr className="widget-separator" />
    <ComplianceBarChart data={ControlPerCompliance} />
  </Widget>
</Col>


    <Col xs={12} xl={4} className="dash-col">
    <Widget className="custom-widget">
      <h5 className="widget-title">{t("Dashboard.QuestionConductedTime")}</h5>
      <hr className="widget-separator" />
      <LineChartSurvey data={Array.isArray(surveyConductedTime) ? surveyConductedTime : []} />
    </Widget>
    </Col>

    <Col xs={12} xl={4} className="dash-col">
      <Widget className="custom-widget">
        <h5 className="widget-title">{t("Dashboard.Compliance_Progress_Over_Time")}</h5>
        <hr className="widget-separator" />
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div>Error: {error}</div>
        ) : (
          <ComplianceProgressChart data={complianceDataStatus} />
        )}
      </Widget>
    </Col>
          </Row>
          )}

          {/* Team-wide activity — every user's/manager's recent actions and survey
              assignments. Not scoped to the viewer, so it's gated the same way as
              the `workload` widget: admins/managers/supervisors only, hidden for
              the personal `user` role. */}
          {chartTab === 'overview' && (isAdmin || roleConfig.showTeamView) && (
          <Row className="mt-3 dash-team-row">
            {/* xl (>=1200px) side by side, stacked below — at md the two
                tables were squeezed to ~half a laptop screen and overflowed. */}
            <Col xs={12} xl={6} className="dash-col">
              <Widget
                title={
                  <div className="dash-card-header">
                    <div className="dash-card-heading">
                      <h5 className="dash-card-title">
                        <i className="fa fa-user dash-card-title-icon" /> Team Compliance Tasks
                      </h5>
                      <div className="dash-card-subtitle">
                        Full overview of compliance tasks across the team, by supervisor/manager function
                      </div>
                    </div>
                    <button type="button" className="dash-view-all-btn" onClick={() => setTeamTasksDrillOpen(true)}>
                      View All
                    </button>
                  </div>
                }
                className="d-flex flex-column h-100 custom-widget dash-table-card"  // Ensures the widget takes full height
              >

  <div className="dash-table-scroll">
  <table className="table table-bordered Users-Compliance-Tasks dash-table mb-0">
    <colgroup>
      <col />
      <col style={{ width: '19%' }} />
      <col style={{ width: '19%' }} />
      <col style={{ width: 108 }} />
      <col style={{ width: 118 }} />
    </colgroup>
    <thead>
      <tr>
        <th>{t('Task Name')}</th>
        <th>Supervisor Function</th>
        <th>Manager Function</th>
        <th className="dash-col-nowrap">{t('Status')}</th>
        <th>{t('Date & Time')}</th>
      </tr>
    </thead>
    <tbody>
      {complianceActions?.map((item: any) => (
        <tr
          key={item.control_id}
          className="clickable-row"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            // console.log('Compliance action row clicked!', {
           //   control_id: item.control_id,
            //  compliance_id: item.compliance_id,
            //  domain_id: item.domain_id
           // });
            // Navigate to view-control page if we have the necessary data
            if (item.control_id && item.compliance_id && item.domain_id) {
              const targetUrl = `/compliances/view-control/${item.control_id}/${item.compliance_id}/${item.domain_id}`;
              //console.log('Navigating to:', targetUrl);
              navigate(targetUrl);
            } else {
              //console.log('Missing data for navigation:', item);
            }
          }}
          title="Click anywhere on this row to view control details"
        >
          <td><div className="dash-clamp-2" title={item.control?.name || ''}>{item.control?.name}</div></td>
          <td><div className="dash-clamp-2" title={item.supervisor?.name || ''}>{item.supervisor?.name || "N/A"}</div></td>
          <td><div className="dash-clamp-2" title={item.manager?.name || ''}>{item.manager?.name || "N/A"}</div></td>
          <td className="dash-col-nowrap">
            <span className="dash-status-badge" title={item.status || 'N/A'} style={{
              background: item.status === 'Done' ? '#DCFCE7' : item.status === 'In Progress' ? '#DBEAFE' : '#FEE2E2',
              color:      item.status === 'Done' ? '#16A34A' : item.status === 'In Progress' ? '#1D4ED8' : '#DC2626',
            }}>
              {item.status || 'N/A'}
            </span>
          </td>
          <td className="dash-col-date">
            {item.due_date ? new Date(item.due_date).toLocaleString() : '—'}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
  </div>


              </Widget>
            </Col>

            <Col xs={12} xl={6} className="dash-col">
              <Widget
                title={
                  <div className="dash-card-header">
                    <div className="dash-card-heading">
                      <h5 className="dash-card-title">
                        <i className="fa fa-user dash-card-title-icon" /> Survey Assignments
                      </h5>
                      <div className="dash-card-subtitle">
                        Full overview of survey assignments across the team, by user and function
                      </div>
                    </div>
                    <button type="button" className="dash-view-all-btn" onClick={() => setSurveyAssignmentsDrillOpen(true)}>
                      View All
                    </button>
                  </div>
                }
                className="d-flex flex-column h-100 custom-widget dash-table-card"  // Ensures the widget takes full height
              >

      <div className="dash-table-scroll">
      <table className="table table-bordered dash-table mb-0">
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '28%' }} />
          <col />
          <col style={{ width: '22%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th>{t('Surveys')}</th>
            <th>Function(s)</th>
          </tr>
        </thead>
        <tbody>
          {assignments?.length > 0 ? (
            assignments?.map((entry) => {
              const functionNames = ((entry as any).publicSurvey?.publicSurveyFunctions || [])
                .map((pf: any) => pf.Function?.name)
                .filter(Boolean)
                .join(', ');
              return (
              <tr
                key={entry.id}
                className="clickable-row"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if ((entry as any).publicSurvey?.id) {
                    navigate(`/assessment-survey/${(entry as any).publicSurvey.id}`);
                  }
                }}
                title="Click to view survey"
              >
                <td><div className="dash-clamp-2" title={entry.user?.name || ''}>{entry.user?.name || "N/A"}</div></td>
                <td className="dash-col-wrap-any">{entry.user?.email || "N/A"}</td>
                <td><div className="dash-clamp-2" title={entry.publicSurvey?.name_en || ''}>{entry.publicSurvey?.name_en || "N/A"}</div></td>
                <td><div className="dash-clamp-2" title={functionNames}>{functionNames || "N/A"}</div></td>
              </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4} className="text-center dash-table-empty">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

</Widget>
</Col>

          </Row>
          )}

          <DrillDownModal
            isOpen={teamTasksDrillOpen}
            onClose={() => setTeamTasksDrillOpen(false)}
            title="Team Compliance Tasks — All Activity"
            subtitle="Every non-deleted-compliance task, by supervisor/manager function"
            columns={TEAM_TASKS_DRILL_COLUMNS}
            rows={teamTasksDrillRows}
            fetchPage={fetchTeamTasksPage}
          />

          <DrillDownModal
            isOpen={surveyAssignmentsDrillOpen}
            onClose={() => setSurveyAssignmentsDrillOpen(false)}
            title="Survey Assignments — All Activity"
            subtitle="Every survey assignment, by user and function"
            columns={SURVEY_ASSIGNMENTS_DRILL_COLUMNS}
            rows={surveyAssignmentsDrillRows}
            fetchPage={fetchSurveyAssignmentsPage}
          />

    {/*activeTab == "Dashboards" && (
  loading ? (
    <div className="loading-screen" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
      <div><LoadingScreen/></div>
    </div>
  ) : (
    <>
      <style>
        {`
          .uBIXt.emotion-65tole.ef7g9s29 {
            display: none !important;
          }
          
          /* بديل إذا لم يعمل المحدد السابق */
         /* [class*="uBIXt"][class*="emotion-65tole"][class*="ef7g9s29"] {
            display: none !important;
          }
        `}
      </style>
      <div className="App" style={{ width: '100%', height: '800px' }}>
        <div ref={dashboardRef5} id="dashboard-container2" style={{ width: '100%', height: '800px' }}>
        </div>
      </div>
    </>
  )
)*/}

 

        </>

        {/* In Progress Modal */}
{/* Implemented Modal */}
{/* Not Implemented Modal */}
{/* Completed Tasks Modal */}
{/* Survey Responses Modal */}
{/* User Participate Surveys Modal */}
{/* Total Surveys Modal */}
</div>
    );
  }

export default withTranslation()(Dashboard);
